import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileIcon } from '../../components/icons/ChatCategoryIcons.js';
import {
  deleteSaenggibuRecord,
  deleteSaenggibuVersion,
  getSaenggibuHistory,
  getSaenggibuHistoryExportData,
  getSaenggibuRecord,
  refineSaenggibuContent,
  updateSaenggibuRecordMeta,
  updateBatchSaenggibuResult,
  updateBatchSaenggibuStudentName,
  updateMultiClassSaenggibuVersion,
  updateSaenggibuVersion,
  updateSaenggibuVersionLabel,
} from '../../api/saenggibuApi';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../../components/Toast';
import SaenggibuContentEditor from '../../components/saenggibu/SaenggibuContentEditor';
import SaenggibuResultMoreMenu from '../../components/saenggibu/SaenggibuResultMoreMenu';
import SaenggibuEvidenceLoader from '../../components/saenggibu/evidence/SaenggibuEvidenceLoader';
import SaenggibuEvidenceText, { SaenggibuEvidenceSummary, useShowEvidenceNumbers } from '../../components/saenggibu/evidence/SaenggibuEvidenceText';
import SaenggibuEvidenceDialog from '../../components/saenggibu/evidence/SaenggibuEvidenceDialog';
import { calculateNEISBytes, getSaenggibuMaxBytes } from '../../utils/neisBytes';
import { trackSaenggibuUxEvent } from '../../utils/eventTracker';
import { hasExplicitSimilarLevelDistribution } from '../../utils/similarSentenceLaunch';
import * as XLSX from 'xlsx';
import './SaenggibuHistory.css';
import './InspectorHistory.css';

// 필터 탭 목록 (전체 + 개별 카테고리)
const FILTER_TABS = [
  { key: '전체',     label: '전체' },
  { key: '행동발달',  label: '행발' },
  { key: '교과',     label: '교과' },
  { key: '교과_고등', label: '교과(고)' },
  { key: '창체',     label: '창체' },
  { key: '동아리',   label: '동아리' },
  { key: '진로',     label: '진로' },
  { key: '비슷한문장', label: '비슷' },
];

const getLevelBadgeStyle = (level) => {
  if (level === '상' || level === '매우잘함') {
    return { color: '#1d4ed8', backgroundColor: '#dbeafe' };
  }
  if (level === '중' || level === '잘함') {
    return { color: '#047857', backgroundColor: '#d1fae5' };
  }
  if (level === '보통') {
    return { color: '#374151', backgroundColor: '#f3f4f6' };
  }
  return { color: '#b91c1c', backgroundColor: '#fee2e2' };
};

const getDefaultTargetBytes = (category) => {
  if (category === '창체') return 800;
  return 800;
};

const getSafeTargetBytes = (record) => {
  const category = record?.category || '행동발달';
  const maxBytes = getSaenggibuMaxBytes(category);
  const rawTarget = Number(record?.target_word_count || record?.target_bytes || 0);
  if (Number.isFinite(rawTarget) && rawTarget >= 100) {
    return Math.min(rawTarget, maxBytes);
  }
  return getDefaultTargetBytes(category);
};

const CATEGORY_LABELS = {
  '행동발달': '행동발달',
  '교과': '교과세특',
  '교과_고등': '세특(고)',
  '창체': '창체',
  '동아리': '동아리',
  '진로': '진로',
  '비슷한문장': '비슷한 문장',
};

const cleanPromptPreview = (prompt, fallback = '입력 내용 없음') => {
  const cleaned = (prompt || '')
    .replace(/\[.*?\]/g, '')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return fallback;
  return cleaned.length > 72 ? `${cleaned.substring(0, 72)}...` : cleaned;
};

const formatLevelCounts = (record) => {
  const counts = record?.level_counts || {};
  if (record?.school_level === '초등') {
    return `매우잘함 ${counts.excellent || 0} · 잘함 ${counts.good || 0} · 보통 ${counts.basic || 0} · 노력요함 ${counts.needsSupport || 0}`;
  }
  return `상 ${counts.high || 0} · 중 ${counts.mid || 0} · 하 ${counts.low || 0}`;
};

const hasLevelCounts = (record) => Object.values(record?.level_counts || {})
  .some(value => Number(value) > 0);

const shouldShowResultLevel = (record, level) => {
  if (!level) return false;
  const isSimilarRecord = record?.mode === 'similar' || record?.category === '비슷한문장';
  return !isSimilarRecord || hasExplicitSimilarLevelDistribution(record?.level_distribution);
};

const getRecordCountLabel = (record) => {
  if (record?.mode === 'batch') {
    const total = record.total_students || record.successful_count || record.total_versions || 0;
    return total > 0 ? `${total}명` : '일괄';
  }
  if (record?.mode === 'multi_class') {
    const total = record.total_students || record.total_versions || 0;
    return total > 0 ? `${total}명` : `${record.class_count || record.class_results?.length || 0}개 반`;
  }
  const versions = record?.total_versions || record?.generation_count || 1;
  if (record?.mode === 'similar' || record?.category === '비슷한문장') {
    return `${versions}문장`;
  }
  return `${versions}버전`;
};

const HISTORY_PAGE_SIZE = 200;
const MAX_LOADED_HISTORY_RECORDS = 1000;
const MAX_HISTORY_EXPORT_RECORDS = 100;
const MAX_HISTORY_EXPORT_ROWS = 1000;

const estimateSaenggibuExportRows = (records) => records.reduce((sum, record) => {
  if (record?.mode === 'batch') {
    return sum + Math.max(1, Number(record.total_students || record.successful_count || record.total_versions || 0));
  }
  return sum + Math.max(1, Number(record.total_versions || record.generation_count || 1));
}, 0);

const EVIDENCE_MODES = new Set(['single', 'batch', 'multi_class']);

const supportsEvidence = (record) => (
  EVIDENCE_MODES.has(record?.mode || 'single')
  && record?.category !== '비슷한문장'
  && !record?.mode?.startsWith?.('similar')
);

/** 근거 보기 결과 키: 단일 v{버전}, 일괄 s{학생}, 학급별 c{반}v{순번}. */
const evidenceKeyFor = (target) => {
  if (!target) return '';
  if (target.mode === 'batch') return `s${target.studentIndex}`;
  if (target.mode === 'multi_class') return `c${target.classIndex}v${target.versionIndex}`;
  return `v${target.versionNumber}`;
};

const getContentEditKey = (target) => {
  if (!target?.recordId || !target?.mode) return '';
  if (target.mode === 'batch') {
    return `${target.recordId}:batch:${target.studentIndex}`;
  }
  if (target.mode === 'multi_class') {
    return `${target.recordId}:multi_class:${target.classIndex}:${target.versionIndex}`;
  }
  return `${target.recordId}:single:${target.versionNumber}`;
};

// ── 날짜 그룹 헬퍼 ──
const getDateGroup = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  if (date >= today) return '오늘';
  if (date >= yesterday) return '어제';
  if (date >= weekAgo) return '이번 주';
  if (date >= monthStart) return '이번 달';
  return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' });
};

// ── 년도/학기 필터 헬퍼 ──
// 년도 목록: 캘린더 기준 (2026년 2월 → 2026으로 표시)
// 학기 필터: 날짜 범위 (2025년 2학기 = 2025.09 ~ 2026.02)
const getCalendarYear = (dateStr) => new Date(dateStr).getFullYear();

const isInSemesterRange = (dateStr, year, semester) => {
  const date = new Date(dateStr);
  if (semester === 1) {
    // 1학기: 해당 년도 3월 1일 ~ 8월 31일
    const start = new Date(year, 2, 1);
    const end = new Date(year, 8, 0, 23, 59, 59);
    return date >= start && date <= end;
  } else {
    // 2학기: 해당 년도 9월 1일 ~ 다음해 2월 말일
    const start = new Date(year, 8, 1);
    const end = new Date(year + 1, 2, 0, 23, 59, 59);
    return date >= start && date <= end;
  }
};

const SaenggibuHistory = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { subscriptionStatus } = useSubscription();
  const isProUser = Boolean(subscriptionStatus?.isPro);
  const { showToast } = useToast();

  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('전체');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('전체');
  const [selectedSemester, setSelectedSemester] = useState('전체');
  const [activeBatchStudentIdx, setActiveBatchStudentIdx] = useState({});
  const [evidenceApis, setEvidenceApis] = useState({});
  const [historyEvidence, setHistoryEvidence] = useState(null);
  const [showEvidenceNumbers, setShowEvidenceNumbers] = useShowEvidenceNumbers();
  const handleEvidenceApi = useCallback((recordId, api) => {
    setEvidenceApis((prev) => {
      if (prev[recordId] === api) return prev;
      if (!api) {
        const next = { ...prev };
        delete next[recordId];
        return next;
      }
      return { ...prev, [recordId]: api };
    });
  }, []);
  const [showVersionDeleteConfirm, setShowVersionDeleteConfirm] = useState(null);
  const [selectedSubFilter, setSelectedSubFilter] = useState('전체');
  const [showAllSubChips, setShowAllSubChips] = useState(false);
  const [batchRefineTarget, setBatchRefineTarget] = useState(null);
  const [batchRefinePresetId, setBatchRefinePresetId] = useState('history-batch-strength');
  const [batchRefineMode, setBatchRefineMode] = useState('tone_up');
  const [batchRefineInstruction, setBatchRefineInstruction] = useState('');
  const [batchRefinePreview, setBatchRefinePreview] = useState(null);
  const [batchRefinePreviewDraft, setBatchRefinePreviewDraft] = useState('');
  const [batchRefinePreviewEditing, setBatchRefinePreviewEditing] = useState(false);
  const [batchRefineLoading, setBatchRefineLoading] = useState(false);
  const [batchRefineApplying, setBatchRefineApplying] = useState(false);
  const refineActionInFlightRef = useRef(false);
  const [manuallyEditedKeys, setManuallyEditedKeys] = useState(() => new Set());
  const [editingRecordTitleId, setEditingRecordTitleId] = useState(null);
  const [editingRecordTitleValue, setEditingRecordTitleValue] = useState('');
  const [savingRecordTitleId, setSavingRecordTitleId] = useState(null);
  const [editingBatchStudentName, setEditingBatchStudentName] = useState(null);
  const [editingBatchStudentNameValue, setEditingBatchStudentNameValue] = useState('');
  const [savingBatchStudentName, setSavingBatchStudentName] = useState(null);
  const [editingVersionLabel, setEditingVersionLabel] = useState(null);
  const [editingVersionLabelValue, setEditingVersionLabelValue] = useState('');
  const [savingVersionLabel, setSavingVersionLabel] = useState(null);
  const [contentEditTarget, setContentEditTarget] = useState(null);
  const [contentEditDraft, setContentEditDraft] = useState('');
  const [savingContentEditKey, setSavingContentEditKey] = useState(null);

  // ── 날짜 포맷 (간결하게) ──
  const formatDate = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (d >= today) {
      return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    }
    if (d.getFullYear() !== now.getFullYear()) {
      return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'numeric', day: 'numeric' });
    }
    return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  // ── 사용 가능한 년도 목록 ──
  // ── 사용 가능한 년도 목록 (캘린더 기준) ──
  const availableYears = useMemo(() => {
    const yearSet = new Set();
    historyData.forEach(r => yearSet.add(getCalendarYear(r.created_at)));
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [historyData]);

  // ── 1단계: 년도/학기 + 검색 필터 (최상위, 모든 하위 필터에 영향) ──
  const baseFilteredData = useMemo(() => {
    let data = historyData;

    if (selectedAcademicYear !== '전체') {
      const yearNum = parseInt(selectedAcademicYear, 10);
      if (selectedSemester === '전체') {
        data = data.filter(r => getCalendarYear(r.created_at) === yearNum);
      } else {
        const semNum = parseInt(selectedSemester, 10);
        data = data.filter(r => isInSemesterRange(r.created_at, yearNum, semNum));
      }
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      data = data.filter(r =>
        (r.custom_title || '').toLowerCase().includes(q) ||
        (r.prompt || '').toLowerCase().includes(q) ||
        (r.preview || '').toLowerCase().includes(q) ||
        (r.primary_content || '').toLowerCase().includes(q) ||
        r.versions?.some(v => (v.custom_label || '').toLowerCase().includes(q)) ||
        (r.mode === 'batch' && r.student_names?.some(name =>
          (name || '').toLowerCase().includes(q)
        )) ||
        (r.mode === 'batch' && r.batch_results?.some(s =>
          (s.student_name || '').toLowerCase().includes(q) ||
          (s.content || '').toLowerCase().includes(q)
        ))
      );
    }

    return data;
  }, [historyData, selectedAcademicYear, selectedSemester, searchQuery]);

  // ── 서브카테고리 칩 추출 (baseFilteredData 기반) ──
  const subCategoryChips = useMemo(() => {
    if (selectedFilter === '전체') return [];
    const catData = baseFilteredData.filter(r => (r.category || '행동발달') === selectedFilter);
    const counts = {};

    catData.forEach(r => {
      if (selectedFilter === '행동발달' || selectedFilter === '창체') {
        (r.emphasize_points || []).forEach(p => {
          counts[p] = (counts[p] || 0) + 1;
        });
      } else {
        const sub = r.sub_category;
        if (sub) counts[sub] = (counts[sub] || 0) + 1;
      }
    });

    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ label, count }));
  }, [baseFilteredData, selectedFilter]);

  // ── 2단계: 카테고리 → 서브카테고리 필터 ──
  const filteredData = useMemo(() => {
    let data = baseFilteredData;

    if (selectedFilter !== '전체') {
      data = data.filter(r => (r.category || '행동발달') === selectedFilter);
    }

    if (selectedSubFilter !== '전체' && selectedFilter !== '전체') {
      if (selectedFilter === '행동발달' || selectedFilter === '창체') {
        data = data.filter(r => (r.emphasize_points || []).includes(selectedSubFilter));
      } else {
        data = data.filter(r => r.sub_category === selectedSubFilter);
      }
    }

    return data;
  }, [baseFilteredData, selectedFilter, selectedSubFilter]);

  // ── 날짜 그룹핑 ──
  const groupedData = useMemo(() => {
    const groups = [];
    let currentGroup = null;
    filteredData.forEach(record => {
      const group = getDateGroup(record.created_at);
      if (group !== currentGroup) {
        groups.push({ type: 'header', label: group });
        currentGroup = group;
      }
      groups.push({ type: 'record', data: record });
    });
    return groups;
  }, [filteredData]);

  // ── 카테고리별 개수 (년도/학기/검색 반영) ──
  const categoryCounts = useMemo(() => {
    const counts = {};
    FILTER_TABS.forEach(t => { counts[t.key] = 0; });
    counts['전체'] = baseFilteredData.length;
    baseFilteredData.forEach(r => {
      const cat = r.category || '행동발달';
      if (counts[cat] !== undefined) counts[cat]++;
    });
    return counts;
  }, [baseFilteredData]);

  // ── 데이터 로드 ──
  const loadHistory = useCallback(async (page = 1, append = false) => {
    try {
      setIsLoading(!append);
      const offset = (page - 1) * HISTORY_PAGE_SIZE;
      const response = await getSaenggibuHistory(HISTORY_PAGE_SIZE, offset);
      const newData = Array.isArray(response) ? response : response.records || response.data || [];
      if (append) {
        setHistoryData(prev => [...prev, ...newData]);
      } else {
        setHistoryData(newData);
      }
      setHasMore(response.has_more !== undefined ? response.has_more : newData.length === HISTORY_PAGE_SIZE);
    } catch (error) {
      console.error('히스토리 로드 실패:', error);
      showToast('기록을 불러오지 못했어요');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (auth.currentUser && typeof auth.currentUser.getIdToken === 'function') {
      loadHistory();
      return;
    }
    setHistoryData([]);
    setHasMore(false);
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.currentUser]);

  // ── 체크박스 ──
  const handleToggleSelect = useCallback((id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    const targetIds = filteredData.map(r => r.id);
    const allSelected = targetIds.length > 0 && targetIds.every(id => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : targetIds);
  }, [filteredData, selectedIds]);

  // ── 삭제 ──
  const handleOpenDeleteConfirm = useCallback(() => {
    if (selectedIds.length === 0) { showToast('삭제할 기록을 선택해주세요'); return; }
    setShowDeleteConfirm(true);
  }, [selectedIds, showToast]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.length === 0) return;
    const idsToDelete = [...selectedIds];
    const count = idsToDelete.length;
    const prevData = historyData;

    // 낙관적 업데이트: UI 즉시 반영
    setHistoryData(prev => prev.filter(r => !idsToDelete.includes(r.id)));
    setSelectedIds([]);
    setShowDeleteConfirm(false);
    showToast(`${count}개 기록을 삭제했어요`);

    try {
      await Promise.all(idsToDelete.map(id => deleteSaenggibuRecord(id)));
    } catch (error) {
      console.error('기록 삭제 실패:', error);
      setHistoryData(prevData);
      showToast('삭제 중 문제가 생겼어요. 다시 시도해주세요');
    }
  }, [selectedIds, historyData, showToast]);

  // ── 카드 확장 ──
  const toggleExpand = useCallback(async (recordId) => {
    if (expandedIds.includes(recordId)) {
      setExpandedIds(prev => prev.filter(id => id !== recordId));
      return;
    }
    const record = historyData.find(r => r.id === recordId);

    // batch/multi_class 모드: 상세 결과가 이미 있으면 바로 확장, 없으면 상세 로드
    if (record?.mode === 'batch' || record?.mode === 'multi_class') {
      const hasDetailedResults = record.mode === 'batch'
        ? record.batch_results?.length > 0
        : record.class_results?.some(classItem => classItem.versions?.length > 0);
      if (hasDetailedResults) {
        setExpandedIds(prev => [...prev, recordId]);
        if (activeBatchStudentIdx[recordId] === undefined) {
          setActiveBatchStudentIdx(prev => ({ ...prev, [recordId]: 0 }));
        }
      } else {
        try {
          const response = await getSaenggibuRecord(recordId);
          const rd = response.record || response.data || response;
          setHistoryData(prev => prev.map(r => r.id === recordId ? { ...r, ...rd } : r));
          setExpandedIds(prev => [...prev, recordId]);
          setActiveBatchStudentIdx(prev => ({ ...prev, [recordId]: 0 }));
        } catch (error) {
          console.error('상세 정보 로드 실패:', error);
          showToast('상세 정보를 불러오지 못했어요');
        }
      }
      return;
    }

    // single 모드: 기존 로직
    if (record?.versions?.length > 0) {
      setExpandedIds(prev => [...prev, recordId]);
    } else {
      try {
        const response = await getSaenggibuRecord(recordId);
        const rd = response.record || response.data || response;
        setHistoryData(prev => prev.map(r => r.id === recordId ? { ...r, ...rd, versions: rd.versions || [] } : r));
        setExpandedIds(prev => [...prev, recordId]);
      } catch (error) {
        console.error('상세 정보 로드 실패:', error);
        showToast('상세 정보를 불러오지 못했어요');
      }
    }
  }, [expandedIds, historyData, showToast, activeBatchStudentIdx]);

  // ── 복사 ──
  const handleCopy = async (text, label = '내용', meta = {}) => {
    try {
      await navigator.clipboard.writeText(text);
      trackSaenggibuUxEvent('saenggibu_copy', {
        saenggibu_category: meta.saenggibu_category,
        is_batch: meta.is_batch === true,
        version_index: meta.version_index,
      });
      showToast(`${label}을 복사했어요`);
    } catch { showToast('복사에 실패했어요'); }
  };

  const getBatchRefinePresets = useCallback((category) => {
    const common = [
      {
        id: 'history-batch-shorter',
        mode: 'reduce_bytes',
        label: '조금만 줄여줘',
        instruction: '내용은 좋은데 조금 길어. 핵심은 유지하고 조금만 줄여줘.'
      },
      {
        id: 'history-batch-plain',
        mode: 'tone_neutral',
        label: '평범하게 정리',
        instruction: '좋은데 조금 과한 느낌이 있어. 평범한 생기부 문장처럼 자연스럽게 정리해줘.'
      },
    ];

    const byCategory = {
      '행동발달': [
        {
          id: 'history-batch-strength',
          mode: 'tone_up',
          label: '강점 더 살리기',
          instruction: '이 친구의 강점을 더 살려줘. 태도도 올바르고 잘하는 친구야.'
        },
        {
          id: 'history-batch-weakness',
          mode: 'tone_down',
          label: '아쉬운 점 반영',
          instruction: '너무 잘 써줬어. 그런데 이 친구는 태도에서 아쉬운 점이 있어. 너무 좋게만 쓰지 말고, 조금 안 좋은 부분도 자연스럽게 살려서 써줘.'
        },
      ],
      '교과': [
        {
          id: 'history-batch-subject-attitude',
          mode: 'custom',
          label: '수업 태도 살리기',
          instruction: '수업 시간에 보여준 태도와 참여 모습이 더 잘 드러나게 해줘.'
        },
        {
          id: 'history-batch-subject-thinking',
          mode: 'custom',
          label: '사고 과정 보이게',
          instruction: '정답만 잘한 느낌보다 이 과목에서 어떻게 생각하고 접근했는지가 보이게 해줘.'
        },
      ],
      '창체': [
        {
          id: 'history-batch-creative-role',
          mode: 'custom',
          label: '맡은 역할 살리기',
          instruction: '활동에서 맡은 역할과 실제로 한 일이 더 잘 보이게 해줘.'
        },
        {
          id: 'history-batch-creative-community',
          mode: 'tone_up',
          label: '공동체성 강조',
          instruction: '학급이나 공동체에 기여한 모습, 협력하고 배려한 모습이 더 살아나게 해줘.'
        },
      ],
      '동아리': [
        {
          id: 'history-batch-club-role',
          mode: 'custom',
          label: '역할 더 살리기',
          instruction: '동아리에서 맡은 역할과 실제로 수행한 활동이 더 구체적으로 드러나게 해줘.'
        },
        {
          id: 'history-batch-club-growth',
          mode: 'tone_up',
          label: '성장 과정 강조',
          instruction: '활동 과정에서 달라진 점과 협력한 모습을 사실 범위 안에서 더 선명하게 살려줘.'
        },
      ],
      '진로': [
        {
          id: 'history-batch-career-flow',
          mode: 'custom',
          label: '탐색 흐름 살리기',
          instruction: '진로가 정해진 느낌보다 탐색해가는 과정과 고민의 흐름이 보이게 해줘.'
        },
        {
          id: 'history-batch-career-interest',
          mode: 'tone_up',
          label: '관심 분야 연결',
          instruction: '학생의 관심 분야와 활동 내용이 자연스럽게 이어지게 해줘.'
        },
      ],
    };

    const presetCategory = category === '교과_고등' ? '교과' : category;
    return [...(byCategory[presetCategory] || []), ...common];
  }, []);

  const updateBatchStudentInHistory = useCallback((recordId, studentIndex, content, revision = null) => {
    setHistoryData(prev => prev.map(record => {
      if (record.id !== recordId) return record;
      const batchResults = [...(record.batch_results || [])];
      const target = batchResults[studentIndex];
      if (!target) return record;
      const byteCount = calculateNEISBytes(content);
      const existingRevisionHistory = Array.isArray(target.revision_history) ? target.revision_history : [];
      const revisionHistory = revision
        ? [...existingRevisionHistory, revision].slice(-30)
        : existingRevisionHistory;
      batchResults[studentIndex] = {
        ...target,
        content,
        byte_count: byteCount,
        revision_history: revisionHistory,
        revision_count: revisionHistory.length,
        last_revision_instruction: revision?.instruction || target.last_revision_instruction || '',
        is_over_limit: target.target_bytes > 0 ? byteCount > target.target_bytes : target.is_over_limit,
      };
      return {
        ...record,
        batch_results: batchResults,
        generated_content: studentIndex === 0 ? content : record.generated_content,
        word_count: studentIndex === 0 ? byteCount : record.word_count,
      };
    }));
  }, []);

  const updateBatchStudentNameInHistory = useCallback((recordId, studentIndex, studentName, meta = {}) => {
    setHistoryData(prev => prev.map(record => {
      if (record.id !== recordId) return record;
      const batchResults = [...(record.batch_results || [])];
      const target = batchResults[studentIndex];
      if (!target) return record;
      batchResults[studentIndex] = {
        ...target,
        student_name: studentName,
      };
      const studentNames = meta.studentNames || batchResults.map(item => item.student_name || '');
      const preview = meta.preview ?? (
        studentNames.length > 3
          ? `${studentNames.slice(0, 3).join(', ')} 외 ${studentNames.length - 3}명`
          : studentNames.filter(Boolean).join(', ')
      );
      const batchInputContext = record.batch_input_context
        ? {
            ...record.batch_input_context,
            students: Array.isArray(record.batch_input_context.students)
              ? record.batch_input_context.students.map((student, index) => (
                  index === studentIndex ? { ...student, name: studentName } : student
                ))
              : record.batch_input_context.students
          }
        : record.batch_input_context;

      return {
        ...record,
        batch_results: batchResults,
        batch_input_context: batchInputContext,
        student_names: studentNames,
        preview,
        primary_content: preview,
      };
    }));
  }, []);

  const updateSingleVersionLabelInHistory = useCallback((recordId, versionNumber, customLabel) => {
    setHistoryData(prev => prev.map(record => {
      if (record.id !== recordId) return record;
      const versions = (record.versions || []).map(version => (
        version.version === versionNumber
          ? { ...version, custom_label: customLabel }
          : version
      ));
      return { ...record, versions };
    }));
  }, []);

  const updateSingleVersionInHistory = useCallback((recordId, versionNumber, content, revision = null) => {
    setHistoryData(prev => prev.map(record => {
      if (record.id !== recordId) return record;

      const byteCount = calculateNEISBytes(content);
      const updatedAt = new Date().toISOString();
      const versions = (record.versions || []).map((version, index) => {
        const currentVersionNumber = version.version || index + 1;
        if (currentVersionNumber !== versionNumber) return version;
        return {
          ...version,
          version: currentVersionNumber,
          content,
          word_count: byteCount,
          updated_at: updatedAt,
          revision_count: Number(version.revision_count || 0) + (revision ? 1 : 0),
          last_revision_instruction: revision?.instruction || version.last_revision_instruction || '',
        };
      });
      const revisionHistory = revision
        ? [
            ...(record.revision_history || []),
            {
              ...revision,
              version: versionNumber,
              content_preview: content.slice(0, 120),
              updated_at: updatedAt,
            },
          ].slice(-30)
        : (record.revision_history || []);

      return {
        ...record,
        versions,
        revision_history: revisionHistory,
        revision_history_count: revisionHistory.length,
        ...(versionNumber === 1 ? {
          generated_content: content,
          primary_content: content,
          word_count: byteCount,
          primary_word_count: byteCount,
        } : {}),
      };
    }));
  }, []);

  const updateMultiClassStudentInHistory = useCallback((recordId, classIndex, versionIndex, content, revision = null) => {
    setHistoryData(prev => prev.map(record => {
      if (record.id !== recordId) return record;
      const classResults = [...(record.class_results || [])];
      const classItem = classResults[classIndex];
      if (!classItem) return record;
      const versions = [...(classItem.versions || [])];
      const target = versions[versionIndex];
      if (!target) return record;
      const byteCount = calculateNEISBytes(content);
      const revisionHistory = revision
        ? [...(target.revision_history || []), revision].slice(-30)
        : (target.revision_history || []);
      versions[versionIndex] = {
        ...target,
        content,
        word_count: byteCount,
        revision_history: revisionHistory,
        revision_count: revisionHistory.length,
        last_revision_instruction: revision?.instruction || target.last_revision_instruction || '',
      };
      classResults[classIndex] = { ...classItem, versions };
      return {
        ...record,
        class_results: classResults,
        generated_content: classIndex === 0 && versionIndex === 0 ? content : record.generated_content,
        word_count: classIndex === 0 && versionIndex === 0 ? byteCount : record.word_count,
      };
    }));
  }, []);

  const buildBatchRefineOriginalContext = useCallback((record, studentIndex, student) => {
    const inputContext = record.batch_input_context || {};
    const sourceStudent = Array.isArray(inputContext.students) ? inputContext.students[studentIndex] : null;
    const contextParts = [
      `[학생명]\n${student.student_name || sourceStudent?.name || ''}`,
      `[카테고리]\n${record.category || ''}`,
      `[목표 바이트]\n${student.target_bytes || record.target_word_count || ''}`,
    ];

    if (sourceStudent?.content) {
      contextParts.push(`[학생 원자료]\n${sourceStudent.content}`);
    }
    if (inputContext.reference_text) {
      contextParts.push(`[평가계획서 참고자료]\n${inputContext.reference_text}`);
    }
    if (inputContext.teacher_instruction) {
      contextParts.push(`[처음 생성 요청사항]\n${inputContext.teacher_instruction}`);
    }

    return contextParts.join('\n\n');
  }, []);

  const buildMultiClassRefineOriginalContext = useCallback((record, classIndex, versionIndex, version) => {
    const classItem = record.class_results?.[classIndex] || {};
    const levelCounts = record.level_counts || {};
    return [
      `[카테고리]\n${record.category || '교과'}`,
      `[반]\n${classItem.name || `${classIndex + 1}반`}`,
      `[학생 번호]\n${versionIndex + 1}`,
      `[성취수준]\n${version.level || ''}`,
      `[학교급]\n${record.school_level || ''}`,
      `[성취수준별 인원]\n상: ${levelCounts.high || 0}, 중: ${levelCounts.mid || 0}, 하: ${levelCounts.low || 0}`,
      `[목표 바이트]\n${record.target_word_count || ''}`,
      `[최초 입력]\n${record.prompt || ''}`,
    ].join('\n\n');
  }, []);

  const buildSingleRefineOriginalContext = useCallback((record, version) => {
    const contextParts = [
      `[카테고리]\n${record.category || '행동발달'}`,
      `[학교급]\n${record.school_level || ''}`,
      `[세부 항목]\n${record.sub_category || ''}`,
      `[성취수준]\n${version.level || ''}`,
      `[목표 바이트]\n${getSafeTargetBytes(record)}`,
    ];
    if (record.emphasize_points?.length > 0) {
      contextParts.push(`[강조점]\n${record.emphasize_points.join(', ')}`);
    }
    if (record.prompt) {
      contextParts.push(`[최초 입력]\n${record.prompt}`);
    }
    return contextParts.filter(part => !part.endsWith('\n')).join('\n\n');
  }, []);

  const handleOpenBatchRefine = useCallback((record, studentIndex, meta = {}) => {
    if (savingContentEditKey || batchRefineApplying) {
      showToast('저장이 끝난 뒤 AI로 다듬어주세요', 'warning');
      return;
    }
    if (
      contentEditTarget
      && contentEditDraft !== (contentEditTarget.originalContent || '')
    ) {
      showToast('수정 중인 내용을 먼저 저장하거나 취소해주세요', 'warning');
      return;
    }
    setContentEditTarget(null);
    setContentEditDraft('');
    const isSameTarget = batchRefineTarget?.recordId === record.id
      && batchRefineTarget?.studentIndex === studentIndex
      && batchRefineTarget?.classIndex === meta.classIndex
      && batchRefineTarget?.versionIndex === meta.versionIndex;
    if (isSameTarget) {
      setBatchRefineTarget(null);
      setBatchRefinePreview(null);
      setBatchRefinePreviewDraft('');
      setBatchRefinePreviewEditing(false);
      return;
    }

    const defaultPreset = getBatchRefinePresets(record.category || '행동발달')[0];
    setBatchRefineTarget({
      recordId: record.id,
      recordMode: record.mode || 'single',
      studentIndex,
      ...meta,
    });
    setBatchRefinePreview(null);
    setBatchRefinePreviewDraft('');
    setBatchRefinePreviewEditing(false);
    setBatchRefinePresetId(defaultPreset?.id || null);
    setBatchRefineMode(defaultPreset?.mode || 'custom');
    setBatchRefineInstruction(defaultPreset?.instruction || '');
  }, [
    batchRefineApplying,
    batchRefineTarget,
    contentEditDraft,
    contentEditTarget,
    getBatchRefinePresets,
    savingContentEditKey,
    showToast,
  ]);

  const handleStartBatchStudentNameEdit = useCallback((recordId, studentIndex, currentName) => {
    setEditingBatchStudentName({ recordId, studentIndex });
    setEditingBatchStudentNameValue(currentName || '');
  }, []);

  const handleCancelBatchStudentNameEdit = useCallback(() => {
    setEditingBatchStudentName(null);
    setEditingBatchStudentNameValue('');
  }, []);

  const handleSaveBatchStudentNameEdit = useCallback(async () => {
    if (!editingBatchStudentName) return;
    const { recordId, studentIndex } = editingBatchStudentName;
    const nextName = editingBatchStudentNameValue.trim();
    if (!nextName) {
      showToast('학생 이름을 입력해주세요', 'warning');
      return;
    }

    const previousData = historyData;
    setSavingBatchStudentName({ recordId, studentIndex });
    updateBatchStudentNameInHistory(recordId, studentIndex, nextName);

    try {
      const response = await updateBatchSaenggibuStudentName(recordId, studentIndex, nextName);
      updateBatchStudentNameInHistory(recordId, studentIndex, response.student_name || nextName, {
        studentNames: response.student_names,
        preview: response.preview,
      });
      showToast('학생 이름을 저장했어요', 'success');
      setEditingBatchStudentName(null);
      setEditingBatchStudentNameValue('');
    } catch (error) {
      console.error('일괄 생기부 학생 이름 저장 실패:', error);
      setHistoryData(previousData);
      showToast('학생 이름을 저장하지 못했어요. 다시 시도해주세요', 'error');
    } finally {
      setSavingBatchStudentName(null);
    }
  }, [editingBatchStudentName, editingBatchStudentNameValue, historyData, showToast, updateBatchStudentNameInHistory]);

  const handleStartVersionLabelEdit = useCallback((recordId, version) => {
    setEditingVersionLabel({ recordId, versionNumber: version.version });
    setEditingVersionLabelValue(version.custom_label?.trim() || `버전 ${version.version}`);
  }, []);

  const handleCancelVersionLabelEdit = useCallback(() => {
    setEditingVersionLabel(null);
    setEditingVersionLabelValue('');
  }, []);

  const handleSaveVersionLabelEdit = useCallback(async () => {
    if (!editingVersionLabel) return;
    const { recordId, versionNumber } = editingVersionLabel;
    const nextLabel = editingVersionLabelValue.trim();
    const previousData = historyData;
    setSavingVersionLabel({ recordId, versionNumber });
    updateSingleVersionLabelInHistory(recordId, versionNumber, nextLabel);

    try {
      const response = await updateSaenggibuVersionLabel(recordId, versionNumber, nextLabel);
      updateSingleVersionLabelInHistory(recordId, versionNumber, response.custom_label || nextLabel);
      showToast(nextLabel ? '버전 이름을 저장했어요' : '버전 이름을 비웠어요', 'success');
      setEditingVersionLabel(null);
      setEditingVersionLabelValue('');
    } catch (error) {
      console.error('생기부 버전 이름 저장 실패:', error);
      setHistoryData(previousData);
      showToast('버전 이름을 저장하지 못했어요. 다시 시도해주세요', 'error');
    } finally {
      setSavingVersionLabel(null);
    }
  }, [editingVersionLabel, editingVersionLabelValue, historyData, showToast, updateSingleVersionLabelInHistory]);

  const handleStartContentEdit = useCallback((target, currentContent) => {
    if (batchRefineLoading || batchRefineApplying) {
      showToast('AI가 문장을 다듬는 중이에요. 잠시만 기다려주세요', 'warning');
      return;
    }
    if (savingContentEditKey) {
      showToast('저장이 끝난 뒤 다른 문장을 수정해주세요', 'warning');
      return;
    }

    const nextKey = getContentEditKey(target);
    const currentKey = getContentEditKey(contentEditTarget);
    if (nextKey === currentKey) {
      setContentEditTarget(null);
      setContentEditDraft('');
      return;
    }

    if (
      contentEditTarget
      && contentEditDraft !== (contentEditTarget.originalContent || '')
    ) {
      showToast('수정 중인 내용을 먼저 저장하거나 취소해주세요', 'warning');
      return;
    }

    setContentEditTarget({
      ...target,
      originalContent: currentContent || '',
    });
    setContentEditDraft(currentContent || '');
    setBatchRefineTarget(null);
    setBatchRefinePreview(null);
    setBatchRefinePreviewDraft('');
    setBatchRefinePreviewEditing(false);
  }, [
    batchRefineApplying,
    batchRefineLoading,
    contentEditDraft,
    contentEditTarget,
    savingContentEditKey,
    showToast,
  ]);

  const handleCancelContentEdit = useCallback(() => {
    if (savingContentEditKey) return;
    setContentEditTarget(null);
    setContentEditDraft('');
  }, [savingContentEditKey]);

  const handleSelectRecordResultTab = useCallback((recordId, nextIndex) => {
    if ((batchRefineLoading || batchRefineApplying) && batchRefineTarget?.recordId === recordId) {
      showToast('AI가 문장을 다듬는 중이에요. 잠시만 기다려주세요', 'warning');
      return;
    }
    if (savingContentEditKey && contentEditTarget?.recordId === recordId) {
      showToast('수정 내용을 저장하고 있어요. 잠시만 기다려주세요', 'warning');
      return;
    }
    if (
      contentEditTarget?.recordId === recordId
      && contentEditDraft !== (contentEditTarget.originalContent || '')
    ) {
      showToast('수정 중인 내용을 먼저 저장하거나 취소해주세요', 'warning');
      return;
    }

    if (contentEditTarget?.recordId === recordId) {
      setContentEditTarget(null);
      setContentEditDraft('');
    }
    if (batchRefineTarget?.recordId === recordId) {
      setBatchRefineTarget(null);
      setBatchRefinePreview(null);
      setBatchRefinePreviewDraft('');
      setBatchRefinePreviewEditing(false);
    }
    setActiveBatchStudentIdx(prev => ({ ...prev, [recordId]: nextIndex }));
  }, [
    batchRefineLoading,
    batchRefineApplying,
    batchRefineTarget,
    contentEditDraft,
    contentEditTarget,
    savingContentEditKey,
    showToast,
  ]);

  const handleSaveContentEdit = useCallback(async () => {
    if (!contentEditTarget || savingContentEditKey) return;

    const content = contentEditDraft.trim();
    if (!content) {
      showToast('저장할 생기부 내용을 입력해주세요', 'warning');
      return;
    }
    if (content === (contentEditTarget.originalContent || '').trim()) {
      setContentEditTarget(null);
      setContentEditDraft('');
      showToast('바뀐 내용이 없어요', 'info');
      return;
    }
    if (!['single', 'batch', 'multi_class'].includes(contentEditTarget.mode)) {
      showToast('이 기록은 아직 직접 수정할 수 없어요', 'warning');
      return;
    }

    const editKey = getContentEditKey(contentEditTarget);
    const previousData = historyData;
    const revision = {
      mode: 'manual_edit',
      instruction: '교사가 기록에서 직접 문장을 수정함',
      before_bytes: calculateNEISBytes(contentEditTarget.originalContent || ''),
      after_bytes: calculateNEISBytes(content),
    };

    setSavingContentEditKey(editKey);
    if (contentEditTarget.mode === 'multi_class') {
      updateMultiClassStudentInHistory(
        contentEditTarget.recordId,
        contentEditTarget.classIndex,
        contentEditTarget.versionIndex,
        content,
        revision
      );
    } else if (contentEditTarget.mode === 'batch') {
      updateBatchStudentInHistory(
        contentEditTarget.recordId,
        contentEditTarget.studentIndex,
        content,
        revision
      );
    } else {
      updateSingleVersionInHistory(
        contentEditTarget.recordId,
        contentEditTarget.versionNumber,
        content,
        revision
      );
    }

    try {
      if (contentEditTarget.mode === 'multi_class') {
        await updateMultiClassSaenggibuVersion(
          contentEditTarget.recordId,
          contentEditTarget.classIndex,
          contentEditTarget.versionIndex,
          content,
          revision
        );
      } else if (contentEditTarget.mode === 'batch') {
        await updateBatchSaenggibuResult(
          contentEditTarget.recordId,
          contentEditTarget.studentIndex,
          content,
          revision
        );
      } else {
        await updateSaenggibuVersion(
          contentEditTarget.recordId,
          contentEditTarget.versionNumber,
          content,
          revision
        );
      }
      window.dispatchEvent(new CustomEvent('saenggibuCreated', {
        detail: { recordId: contentEditTarget.recordId }
      }));
      const editedRecord = historyData.find((item) => item.id === contentEditTarget.recordId);
      setManuallyEditedKeys((prev) => new Set(prev).add(editKey));
      trackSaenggibuUxEvent('saenggibu_manual_edit', {
        saenggibu_category: editedRecord?.category,
        is_batch: contentEditTarget.mode === 'batch',
        version_index: contentEditTarget.mode === 'single'
          ? Math.max(0, (contentEditTarget.versionNumber || 1) - 1)
          : (contentEditTarget.versionIndex ?? contentEditTarget.studentIndex ?? 0),
      });
      showToast('수정한 내용을 기록에 저장했어요', 'success');
      setContentEditTarget(null);
      setContentEditDraft('');
    } catch (error) {
      console.error('생기부 기록 직접 수정 저장 실패:', error);
      setHistoryData(previousData);
      showToast('내용을 저장하지 못했어요. 다시 시도해주세요', 'error');
    } finally {
      setSavingContentEditKey(null);
    }
  }, [
    contentEditDraft,
    contentEditTarget,
    historyData,
    savingContentEditKey,
    showToast,
    updateBatchStudentInHistory,
    updateMultiClassStudentInHistory,
    updateSingleVersionInHistory,
  ]);

  const handleRequestBatchRefine = useCallback(async (record, studentIndex, student, meta = {}) => {
    const currentContent = student?.content || student?.generated_content || '';
    if (!currentContent.trim()) return;
    if (refineActionInFlightRef.current || batchRefineLoading || batchRefineApplying) {
      showToast('이미 문장을 다듬고 있어요', 'warning');
      return;
    }
    refineActionInFlightRef.current = true;

    const maskedContent = currentContent;
    const maskedInstruction = batchRefineInstruction || '';

    try {
      setBatchRefineLoading(true);
      const recordMode = record.mode || 'single';
      let refineRecord = record;
      if (
        recordMode === 'batch'
        && !record.batch_input_context
        && (record.teacher_instruction_present || record.reference_text_present || record.reference_files_count > 0)
      ) {
        const detailResponse = await getSaenggibuRecord(record.id);
        const detailRecord = detailResponse.record || detailResponse.data || detailResponse;
        refineRecord = { ...record, ...detailRecord };
        setHistoryData(prev => prev.map(item => item.id === record.id ? { ...item, ...detailRecord } : item));
      }
      const response = await refineSaenggibuContent({
        record_id: record.id,
        student_index: recordMode === 'batch' ? studentIndex : undefined,
        version_index: recordMode === 'batch' ? undefined : meta.versionIndex,
        current_content: maskedContent,
        instruction: maskedInstruction,
        mode: batchRefineMode,
        scope: recordMode === 'batch'
          ? 'batch_student'
          : (recordMode === 'multi_class' ? 'multi_class_student' : 'single_version'),
        category: refineRecord.category || '행동발달',
        target_bytes: student.target_bytes || getSafeTargetBytes(refineRecord),
        original_context: recordMode === 'multi_class'
          ? buildMultiClassRefineOriginalContext(refineRecord, meta.classIndex, meta.versionIndex, student)
          : (recordMode === 'batch'
            ? buildBatchRefineOriginalContext(refineRecord, studentIndex, student)
            : buildSingleRefineOriginalContext(refineRecord, student)),
        revision_history: Array.isArray(student.revision_history) ? student.revision_history.slice(-10) : [],
        selected_model: refineRecord.selected_model,
      });
      setBatchRefinePreview({
        recordId: record.id,
        studentIndex,
        classIndex: meta.classIndex,
        versionIndex: meta.versionIndex,
        versionNumber: meta.versionNumber || student.version || studentIndex + 1,
        recordMode,
        before: currentContent,
        after: response.content || '',
        byteCount: response.byte_count || calculateNEISBytes(response.content || ''),
        revision: response.revision || null,
      });
      setBatchRefinePreviewDraft(response.content || '');
      setBatchRefinePreviewEditing(false);
      showToast('AI가 수정안을 만들었어요', 'success');
    } catch (error) {
      console.error('생기부 기록 AI 다듬기 실패:', error);
      showToast(error.message || '생기부 문장을 다듬지 못했어요', 'error');
    } finally {
      refineActionInFlightRef.current = false;
      setBatchRefineLoading(false);
    }
  }, [
    batchRefineApplying,
    batchRefineInstruction,
    batchRefineLoading,
    batchRefineMode,
    buildBatchRefineOriginalContext,
    buildMultiClassRefineOriginalContext,
    buildSingleRefineOriginalContext,
    showToast,
  ]);

  const handleApplyBatchRefinePreview = useCallback(async () => {
    if (
      !batchRefinePreview
      || !batchRefinePreviewDraft.trim()
      || batchRefineApplying
      || refineActionInFlightRef.current
    ) return;
    const {
      recordId,
      studentIndex,
      classIndex,
      versionIndex,
      versionNumber,
      recordMode,
    } = batchRefinePreview;
    const content = batchRefinePreviewDraft.trim();
    const previousData = historyData;
    const revision = batchRefinePreview.revision || null;

    refineActionInFlightRef.current = true;
    setBatchRefineApplying(true);
    if (recordMode === 'multi_class') {
      updateMultiClassStudentInHistory(recordId, classIndex, versionIndex, content, revision);
    } else if (recordMode === 'batch') {
      updateBatchStudentInHistory(recordId, studentIndex, content, revision);
    } else {
      updateSingleVersionInHistory(recordId, versionNumber, content, revision);
    }

    try {
      if (recordMode === 'multi_class') {
        await updateMultiClassSaenggibuVersion(recordId, classIndex, versionIndex, content, revision);
      } else if (recordMode === 'batch') {
        await updateBatchSaenggibuResult(recordId, studentIndex, content, revision);
      } else {
        await updateSaenggibuVersion(recordId, versionNumber, content, revision);
      }
      showToast('수정안을 적용하고 기록에 저장했어요', 'success');
      setBatchRefinePreview(null);
      setBatchRefinePreviewDraft('');
      setBatchRefinePreviewEditing(false);
      setBatchRefineTarget(null);
    } catch (error) {
      console.error('생기부 기록 AI 수정안 저장 실패:', error);
      setHistoryData(previousData);
      showToast('수정안을 저장하지 못했어요. 다시 시도해주세요', 'error');
    } finally {
      refineActionInFlightRef.current = false;
      setBatchRefineApplying(false);
    }
  }, [
    batchRefineApplying,
    batchRefinePreview,
    batchRefinePreviewDraft,
    historyData,
    showToast,
    updateBatchStudentInHistory,
    updateMultiClassStudentInHistory,
    updateSingleVersionInHistory,
  ]);

  const handleReuseBatchRecord = useCallback(async (record) => {
    try {
      let fullRecord = record;
      if (!record.batch_input_context?.students?.length) {
        const response = await getSaenggibuRecord(record.id);
        fullRecord = response.record || response.data || response || record;
      }

      const context = fullRecord.batch_input_context || {};
      const students = Array.isArray(context.students) ? context.students : [];
      if (students.length === 0) {
        showToast('다시 만들 학생 입력 내용을 찾지 못했어요.', 'warning');
        return;
      }

      navigate('/create-saenggibu', {
        state: {
          batchRestore: {
            recordId: fullRecord.id,
            category: fullRecord.category || '행동발달',
            wordCount: getSafeTargetBytes(fullRecord),
            students: students.map((student, index) => ({
              id: student.id || `history_${index + 1}`,
              name: student.name || `학생${index + 1}`,
              existing_content: student.content || '',
              content: student.content || '',
              characteristics: student.characteristics || (student.content || '').slice(0, 42),
              included_columns: Array.isArray(student.included_columns) ? student.included_columns : [],
              confidence: 1,
              warnings: []
            })),
            teacherInstruction: context.teacher_instruction || '',
            referenceText: context.reference_text || '',
            sourceFileName: context.source_file_meta?.filename || '이전 다중생성 기록',
            sourceFileSize: context.source_file_meta?.file_size || 0,
            sourceFileMeta: context.source_file_meta || {},
            structureAnalysis: context.structure_analysis || {},
            achievementStandardRefs: fullRecord.achievement_standard_refs || [],
            achievementStandardKeys: fullRecord.achievement_standard_keys || [],
          }
        }
      });
    } catch (error) {
      console.error('일괄 생성 입력 복원 실패:', error);
      showToast('입력 내용을 다시 불러오지 못했어요.', 'error');
    }
  }, [navigate, showToast]);

  // ── 버전 삭제 ──
  const handleDeleteVersion = useCallback(async (recordId, versionNumber) => {
    try {
      const response = await deleteSaenggibuVersion(recordId, versionNumber);
      if (response.deleted_record) {
        setHistoryData(prev => prev.filter(r => r.id !== recordId));
        setExpandedIds(prev => prev.filter(id => id !== recordId));
        showToast('마지막 버전이라 기록 전체가 삭제되었어요');
      } else {
        setHistoryData(prev => prev.map(record => {
          if (record.id === recordId) {
            const uv = record.versions?.filter(v => v.version !== versionNumber) || [];
            return { ...record, versions: uv, total_versions: uv.length, primary_content: uv[0]?.content || '' };
          }
          return record;
        }));
        showToast(`버전 ${versionNumber}을 삭제했어요`);
      }
    } catch { showToast('버전 삭제 중 문제가 생겼어요'); }
    finally { setShowVersionDeleteConfirm(null); }
  }, [showToast]);

  // ── 더보기 ──
  const handleLoadMore = () => {
    if (historyData.length >= MAX_LOADED_HISTORY_RECORDS) {
      setHasMore(false);
      showToast(`화면에서는 최대 ${MAX_LOADED_HISTORY_RECORDS.toLocaleString()}개 기록까지만 불러올 수 있어요.`, 'warning', 6000);
      return;
    }
    setCurrentPage(p => p + 1);
    loadHistory(currentPage + 1, true);
  };

  // ── 엑셀 다운로드 ──
  const handleExcelDownload = useCallback(async () => {
    const targetData = selectedIds.length > 0
      ? historyData.filter(r => selectedIds.includes(r.id))
      : filteredData;
    if (targetData.length === 0) { showToast('다운로드할 기록이 없어요'); return; }
    if (targetData.length > MAX_HISTORY_EXPORT_RECORDS) {
      showToast(`한 번에 최대 ${MAX_HISTORY_EXPORT_RECORDS}개 기록까지만 다운로드할 수 있어요. 범위를 나눠 선택해주세요.`, 'warning', 8000);
      return;
    }

    const estimatedRows = estimateSaenggibuExportRows(targetData);
    if (estimatedRows > MAX_HISTORY_EXPORT_ROWS) {
      showToast(`예상 ${estimatedRows.toLocaleString()}행으로 너무 커요. 학생 수나 기록 범위를 나눠 선택해주세요.`, 'warning', 8000);
      return;
    }

    let fd = targetData;

    try {
      setIsDownloadingExcel(true);
      showToast('엑셀을 준비하고 있어요...');
      const exportResponse = await getSaenggibuHistoryExportData(fd.map(record => record.id));
      const detailsById = new Map(
        (exportResponse.records || []).map(record => [record.id, record])
      );
      const rwd = fd.map(record => {
        const detail = detailsById.get(record.id);
        return detail ? { ...record, ...detail } : record;
      });
      const excelData = [];
      rwd.forEach(record => {
        const prompt = record.prompt || '';
        const generationType = record.mode === 'batch'
          ? '학급 전체'
          : record.mode === 'multi_class'
            ? '여러 반'
            : (record.mode === 'similar' || record.category === '비슷한문장')
              ? '비슷한 문장'
              : '개별';
        const base = {
          생성유형: generationType,
          카테고리: record.category || '행동발달',
          프롬프트: prompt.substring(0, 150) + (prompt.length > 150 ? '...' : '')
        };

        if (record.mode === 'batch') {
          const students = record.batch_results?.length > 0
            ? record.batch_results
            : [{ student_name: '', content: record.generated_content || '' }];

          students.forEach(student => {
            const content = student.content || student.generated_content || '';
            const byteCount = student.byte_count || calculateNEISBytes(content);
            const targetBytes = student.target_bytes || getSafeTargetBytes(record);
            excelData.push({
              ...base,
              학생명: student.student_name || '',
              성취수준: student.level || '',
              '생기부 내용': content,
              실제바이트: byteCount,
              목표바이트: targetBytes,
              적정: byteCount > targetBytes * 1.2 ? '초과' : byteCount > targetBytes ? '주의' : '적정'
            });
          });
          return;
        }

        if (record.mode === 'multi_class' && record.class_results?.length > 0) {
          record.class_results.forEach((classItem, classIdx) => {
            const className = classItem.name || `${classIdx + 1}반`;
            (classItem.versions || []).forEach((version, versionIdx) => {
              const content = version.content || '';
              const byteCount = version.word_count || calculateNEISBytes(content);
              const targetBytes = getSafeTargetBytes(record);
              excelData.push({
                ...base,
                학생명: version.student_name || `${className} 학생${versionIdx + 1}`,
                성취수준: version.level || '',
                '생기부 내용': content,
                실제바이트: byteCount,
                목표바이트: targetBytes,
                적정: byteCount > targetBytes * 1.2 ? '초과' : byteCount > targetBytes ? '주의' : '적정'
              });
            });
          });
          return;
        }

        const versions = record.versions?.length > 0 ? record.versions : [{ content: record.generated_content || record.primary_content || '' }];
        versions.forEach(v => {
          const content = v.content || '';
          const byteCount = calculateNEISBytes(content);
          const targetBytes = getSafeTargetBytes(record);
          excelData.push({
            ...base,
            학생명: '',
            성취수준: shouldShowResultLevel(record, v.level) ? v.level : '',
            '생기부 내용': content,
            실제바이트: byteCount,
            목표바이트: targetBytes,
            적정: byteCount > targetBytes * 1.2 ? '초과' : byteCount > targetBytes ? '주의' : '적정'
          });
        });
      });
      const ws = XLSX.utils.json_to_sheet(excelData);
      ws['!cols'] = [
        { wch: 12 },
        { wch: 12 },
        { wch: 50 },
        { wch: 12 },
        { wch: 10 },
        { wch: 100 },
        { wch: 12 },
        { wch: 12 },
        { wch: 8 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '생기부 기록');
      XLSX.writeFile(wb, `생기부_기록_${new Date().toISOString().split('T')[0]}.xlsx`);
      showToast(`${fd.length}개 기록이 다운로드 폴더에 저장되었어요`, 'success', 10000);
    } catch { showToast('다운로드 중 문제가 생겼어요. 다시 시도해주세요'); }
    finally { setIsDownloadingExcel(false); }
  }, [historyData, filteredData, selectedIds, showToast]);

  // ── 카테고리 이니셜 ──
  const getCategoryInitial = (category) => {
    const map = { '행동발달': '행', '교과': '교', '교과_고등': '고', '창체': '창', '동아리': '동', '진로': '진' };
    return map[category || '행동발달'] || (category || '행').charAt(0);
  };

  // ── 카드 제목 (프롬프트 요약) ──
  const getCardTitle = (record, options = {}) => {
    if (!options.ignoreCustom && record.custom_title?.trim()) {
      return record.custom_title.trim();
    }
    if (record.mode === 'multi_class') {
      const subject = record.sub_category || record.class_context || '';
      const prefix = subject ? `${subject} 교과세특` : '교과세특';
      return `${prefix} · ${record.class_count || record.class_results?.length || 0}개 반 ${record.total_students || record.total_versions || 0}명`;
    }
    if (record.mode === 'batch') {
      const category = CATEGORY_LABELS[record.category || '행동발달'] || record.category || '생기부';
      return `${category} 일괄 생성 · ${record.total_students || record.successful_count || record.total_versions || 0}명`;
    }
    if (record.category === '교과' && record.class_index && record.total_classes) {
      const subject = record.sub_category || record.class_context || '';
      return `${subject ? `${subject} ` : ''}교과세특 · ${record.class_index}/${record.total_classes}반`;
    }
    if (record.category === '교과' || record.category === '교과_고등') {
      const subject = record.sub_category || '';
      const label = record.category === '교과_고등' ? '세특(고)' : '교과세특';
      return subject ? `${subject} ${label}` : label;
    }
    if (record.category === '행동발달' || record.category === '창체') {
      const emphasis = (record.emphasize_points || []).slice(0, 2).join(' · ');
      return emphasis ? `${CATEGORY_LABELS[record.category]} · ${emphasis}` : CATEGORY_LABELS[record.category];
    }
    if (record.category === '동아리') {
      return record.sub_category ? `${record.sub_category} 동아리` : '동아리';
    }
    if (record.category === '진로') {
      return record.sub_category ? `진로 · ${record.sub_category}` : '진로';
    }
    if (record.category === '비슷한문장' || record.mode === 'similar') {
      return `비슷한 문장 · ${record.total_versions || record.generation_count || 0}개`;
    }
    return CATEGORY_LABELS[record.category] || cleanPromptPreview(record.prompt, '생기부 기록');
  };

  const getCardSubtitle = (record) => {
    if (record.custom_title?.trim()) {
      const generatedTitle = getCardTitle(record, { ignoreCustom: true });
      if (record.mode === 'batch') {
        return [generatedTitle, record.preview || record.primary_content].filter(Boolean).join(' · ');
      }
      return [generatedTitle, cleanPromptPreview(record.prompt, '')].filter(Boolean).join(' · ');
    }
    if (record.mode === 'batch') {
      return record.preview || record.primary_content || '학생별 입력 내용으로 만들었어요';
    }
    if (record.mode === 'multi_class' || (record.category === '교과' && hasLevelCounts(record))) {
      return formatLevelCounts(record);
    }
    const promptPreview = cleanPromptPreview(record.prompt);
    const fileText = record.uploaded_files_count > 0 ? `파일 ${record.uploaded_files_count}개 참고` : '';
    const revisionText = record.revision_history_count > 0 ? `${record.revision_history_count}회 다듬음` : '';
    return [promptPreview, fileText, revisionText].filter(Boolean).join(' · ');
  };

  // ── 모델명 ──
  const getModelLabel = (model) => model === 'gemini-2.5-flash-lite' ? '빠른 모델' : '고급 모델';

  const getExpandedMetaItems = (record, baseItems = []) => ([
    ...baseItems,
    getModelLabel(record.selected_model),
    record.uploaded_files_count > 0 ? `파일 ${record.uploaded_files_count}개 참고` : '',
    record.reference_files_count > 0 ? `참고 파일 ${record.reference_files_count}개` : '',
    record.is_regeneration ? '다시 생성한 기록' : '',
    record.revision_history_count > 0 ? `${record.revision_history_count}회 다듬음` : '',
    record.parse_warnings_count > 0 ? `파일 확인 필요 ${record.parse_warnings_count}건` : '',
  ].filter(Boolean));

  const getVersionLabel = (version) => {
    const defaultLabel = `버전 ${version.version || 1}`;
    return version.custom_label?.trim() || defaultLabel;
  };

  const renderExpandedMeta = (items) => (
    <div className="sh-expanded-meta">
      {items.map((item, index) => (
        <React.Fragment key={`${item}-${index}`}>
          {index > 0 && <span className="sh-expanded-meta-sep">·</span>}
          <span>{item}</span>
        </React.Fragment>
      ))}
    </div>
  );

  const handleStartRecordTitleEdit = (record) => {
    setEditingRecordTitleId(record.id);
    setEditingRecordTitleValue(record.custom_title?.trim() || getCardTitle(record, { ignoreCustom: true }));
  };

  const handleCancelRecordTitleEdit = () => {
    setEditingRecordTitleId(null);
    setEditingRecordTitleValue('');
  };

  const handleSaveRecordTitleEdit = async (recordId) => {
    const nextTitle = editingRecordTitleValue.trim();
    const previousData = historyData;
    setSavingRecordTitleId(recordId);
    setHistoryData(prev => prev.map(record => (
      record.id === recordId ? { ...record, custom_title: nextTitle } : record
    )));

    try {
      const response = await updateSaenggibuRecordMeta(recordId, { custom_title: nextTitle });
      setHistoryData(prev => prev.map(record => (
        record.id === recordId ? { ...record, custom_title: response.custom_title || nextTitle } : record
      )));
      showToast(nextTitle ? '기록 이름을 저장했어요' : '기록 이름을 비웠어요');
      setEditingRecordTitleId(null);
      setEditingRecordTitleValue('');
    } catch (error) {
      console.error('생기부 기록 이름 저장 실패:', error);
      setHistoryData(previousData);
      showToast('기록 이름을 저장하지 못했어요. 다시 시도해주세요', 'error');
    } finally {
      setSavingRecordTitleId(null);
    }
  };

  const renderEditableContent = (record, target, currentContent, requestedTargetBytes = null) => {
    const editKey = getContentEditKey(target);
    const isEditing = editKey === getContentEditKey(contentEditTarget);
    if (!isEditing) {
      const api = supportsEvidence(record) ? evidenceApis[record.id] : null;
      if (!api) return <div className="sh-version-content">{currentContent}</div>;
      const evidenceKey = evidenceKeyFor(target);
      const evidence = api.get(evidenceKey, currentContent);
      const open = (sentence = null) => setHistoryEvidence({ recordId: record.id, target, sentence });
      return (
        <>
          <div className="sh-version-content">
            <SaenggibuEvidenceText
              content={currentContent}
              evidence={evidence}
              showNumbers={showEvidenceNumbers}
              onOpen={open}
            />
          </div>
          <SaenggibuEvidenceSummary
            compact
            status={api.statusOf(evidenceKey, currentContent)}
            evidence={evidence}
            onOpen={() => open(null)}
            showNumbers={showEvidenceNumbers}
            onToggleNumbers={setShowEvidenceNumbers}
          />
        </>
      );
    }

    const targetBytes = Number(requestedTargetBytes) > 0
      ? Number(requestedTargetBytes)
      : getSafeTargetBytes(record);
    const maxBytes = getSaenggibuMaxBytes(record.category || '행동발달');
    const isSaving = savingContentEditKey === editKey;

    return (
      <SaenggibuContentEditor
        isSaving={isSaving}
        maxBytes={maxBytes}
        onCancel={handleCancelContentEdit}
        onChange={setContentEditDraft}
        onSave={handleSaveContentEdit}
        targetBytes={targetBytes}
        value={contentEditDraft}
        variant="compact"
      />
    );
  };

  const renderHistoryRefinePanel = (record, studentIndex, student, meta = {}) => {
    const isTarget = batchRefineTarget?.recordId === record.id
      && batchRefineTarget?.studentIndex === studentIndex
      && batchRefineTarget?.classIndex === meta.classIndex
      && batchRefineTarget?.versionIndex === meta.versionIndex;
    if (!isTarget) return null;

    const hasPreview = batchRefinePreview?.recordId === record.id
      && batchRefinePreview?.studentIndex === studentIndex
      && batchRefinePreview?.classIndex === meta.classIndex
      && batchRefinePreview?.versionIndex === meta.versionIndex;
    const isBusy = batchRefineLoading || batchRefineApplying;

    return (
      <div className="sh-ai-refine-panel" onClick={(event) => event.stopPropagation()}>
        <div className="sh-ai-refine-presets">
          {getBatchRefinePresets(record.category || '행동발달').map(option => (
            <button
              key={option.id}
              type="button"
              className={`sh-ai-refine-chip ${batchRefinePresetId === option.id ? 'sh-ai-refine-chip--active' : ''}`}
              onClick={() => {
                setBatchRefinePresetId(option.id);
                setBatchRefineMode(option.mode);
                setBatchRefineInstruction(option.instruction);
              }}
              title={option.instruction}
              disabled={isBusy}
            >
              {option.label}
            </button>
          ))}
        </div>
        <label className="sh-sr-only" htmlFor={`sh-refine-instruction-${record.id}-${studentIndex}`}>
          AI 다듬기 요청
        </label>
        <textarea
          id={`sh-refine-instruction-${record.id}-${studentIndex}`}
          className="sh-ai-refine-input"
          value={batchRefineInstruction}
          onChange={(event) => {
            setBatchRefinePresetId(null);
            setBatchRefineMode('custom');
            setBatchRefineInstruction(event.target.value);
          }}
          placeholder="추가 요청이 있으면 적어주세요."
          rows={3}
          disabled={isBusy}
        />
        <p className="sh-ai-refine-hint">
          문체, 어투, 분량, 꼭 살릴 표현까지 자유롭게 요청할 수 있어요.
        </p>
        <div className="sh-ai-refine-actions">
          <button
            type="button"
            className="sh-ai-refine-primary"
            onClick={() => handleRequestBatchRefine(record, studentIndex, student, meta)}
            disabled={isBusy}
          >
            {batchRefineLoading ? '다듬는 중...' : '요청하기'}
          </button>
          <button
            type="button"
            className="sh-ai-refine-secondary"
            onClick={() => {
              setBatchRefineTarget(null);
              setBatchRefinePreview(null);
              setBatchRefinePreviewDraft('');
              setBatchRefinePreviewEditing(false);
            }}
            disabled={isBusy}
          >
            닫기
          </button>
        </div>

        {hasPreview && (
          <div className="sh-ai-refine-preview">
            <div className="sh-ai-refine-preview-header">
              <strong>수정안</strong>
              <span>{calculateNEISBytes(batchRefinePreviewDraft || batchRefinePreview.after)}B</span>
            </div>
            {batchRefinePreviewEditing ? (
              <>
                <label className="sh-sr-only" htmlFor={`sh-refine-preview-${record.id}-${studentIndex}`}>
                  AI 수정안 편집
                </label>
                <textarea
                  id={`sh-refine-preview-${record.id}-${studentIndex}`}
                  className="sh-ai-refine-input"
                  value={batchRefinePreviewDraft}
                  onChange={(event) => setBatchRefinePreviewDraft(event.target.value)}
                  rows={6}
                  disabled={batchRefineApplying}
                />
              </>
            ) : (
              <div className="sh-ai-refine-preview-content">
                {batchRefinePreviewDraft || batchRefinePreview.after}
              </div>
            )}
            <div className="sh-ai-refine-actions">
              <button
                type="button"
                className="sh-ai-refine-secondary"
                onClick={() => setBatchRefinePreviewEditing(prev => !prev)}
                disabled={batchRefineApplying}
              >
                {batchRefinePreviewEditing ? '수정 끝내기' : '수정하기'}
              </button>
              <button
                type="button"
                className="sh-ai-refine-dark"
                onClick={handleApplyBatchRefinePreview}
                disabled={batchRefineApplying || !batchRefinePreviewDraft.trim()}
              >
                {batchRefineApplying ? '적용 중...' : '적용하기'}
              </button>
              <button
                type="button"
                className="sh-ai-refine-secondary"
                onClick={() => {
                  setBatchRefinePreview(null);
                  setBatchRefinePreviewDraft('');
                  setBatchRefinePreviewEditing(false);
                }}
                disabled={batchRefineApplying}
              >
                취소
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── 로그인 필요 ──
  if (!auth.currentUser) {
    return (
      <div className="sh-empty">
        <div className="sh-empty-icon"><FileIcon size={28} color="#9ca3af" /></div>
        <h3 className="sh-empty-title">로그인이 필요해요</h3>
        <p className="sh-empty-desc">생기부 기록을 보려면 먼저 로그인해주세요.</p>
        <button className="sh-empty-btn" onClick={() => navigate('/auth?tab=login&redirect=/saenggibu-history')}>
          로그인하기
        </button>
      </div>
    );
  }

  const historyEvidenceEntries = (record) => {
    if (record.mode === 'batch') {
      const index = activeBatchStudentIdx[record.id] || 0;
      return [index, index + 1]
        .map((i) => (record.batch_results?.[i]?.content ? { key: `s${i}`, content: record.batch_results[i].content } : null))
        .filter(Boolean);
    }
    if (record.mode === 'multi_class') {
      const classIndex = activeBatchStudentIdx[record.id] || 0;
      return (record.class_results?.[classIndex]?.versions || [])
        .map((version, versionIndex) => (version?.content ? { key: `c${classIndex}v${versionIndex}`, content: version.content } : null))
        .filter(Boolean);
    }
    return (record.versions || [])
      .map((version, index) => (version?.content ? { key: `v${version.version || index + 1}`, content: version.content } : null))
      .filter(Boolean);
  };

  const historyEvidenceContent = (record, target) => {
    if (!record || !target) return '';
    if (target.mode === 'batch') return record.batch_results?.[target.studentIndex]?.content || '';
    if (target.mode === 'multi_class') return record.class_results?.[target.classIndex]?.versions?.[target.versionIndex]?.content || '';
    const index = (record.versions || []).findIndex((version, i) => (version.version || i + 1) === target.versionNumber);
    return index >= 0 ? record.versions[index].content || '' : '';
  };

  const requestHistoryEvidenceRevision = async (record, target, { scope, sentenceText, sentenceIndex, instruction, targetBytes }) => {
    const content = historyEvidenceContent(record, target);
    const isSentence = scope === 'sentence';
    let originalContext;
    let wholeScope = 'single_version';
    let versionIndex;
    if (target.mode === 'batch') {
      originalContext = buildBatchRefineOriginalContext(record, target.studentIndex, record.batch_results?.[target.studentIndex] || {});
      wholeScope = 'batch_student';
    } else if (target.mode === 'multi_class') {
      const version = record.class_results?.[target.classIndex]?.versions?.[target.versionIndex] || {};
      originalContext = buildMultiClassRefineOriginalContext(record, target.classIndex, target.versionIndex, version);
      wholeScope = 'multi_class_student';
      versionIndex = target.versionIndex;
    } else {
      versionIndex = (record.versions || []).findIndex((version, i) => (version.version || i + 1) === target.versionNumber);
      originalContext = buildSingleRefineOriginalContext(record, record.versions?.[versionIndex] || {});
    }
    const response = await refineSaenggibuContent({
      record_id: record.id,
      student_index: target.mode === 'batch' ? target.studentIndex : undefined,
      version_index: target.mode === 'batch' ? undefined : versionIndex,
      current_content: content,
      instruction,
      mode: isSentence ? 'sentence' : 'custom',
      scope: isSentence ? 'sentence' : wholeScope,
      sentence_text: isSentence ? sentenceText : undefined,
      sentence_index: isSentence ? sentenceIndex : undefined,
      category: record.category || '행동발달',
      target_bytes: targetBytes,
      original_context: originalContext,
      selected_model: record.selected_model,
    });
    return response?.content || '';
  };

  const applyHistoryEvidenceRevision = async (record, target, nextContent) => {
    const before = historyEvidenceContent(record, target);
    const revision = {
      mode: 'evidence_sentence',
      instruction: '근거 보기에서 문장을 고침',
      before_bytes: calculateNEISBytes(before),
      after_bytes: calculateNEISBytes(nextContent),
    };
    const previousData = historyData;
    try {
      if (target.mode === 'multi_class') {
        updateMultiClassStudentInHistory(record.id, target.classIndex, target.versionIndex, nextContent, revision);
        await updateMultiClassSaenggibuVersion(record.id, target.classIndex, target.versionIndex, nextContent, revision);
      } else if (target.mode === 'batch') {
        updateBatchStudentInHistory(record.id, target.studentIndex, nextContent, revision);
        await updateBatchSaenggibuResult(record.id, target.studentIndex, nextContent, revision);
      } else {
        updateSingleVersionInHistory(record.id, target.versionNumber, nextContent, revision);
        await updateSaenggibuVersion(record.id, target.versionNumber, nextContent, revision);
      }
      showToast('바꾼 문장을 기록에 저장했어요', 'success');
      return true;
    } catch (error) {
      console.error('근거 보기 문장 저장 실패:', error);
      setHistoryData(previousData);
      return false;
    }
  };

  const renderHistoryEvidenceDialog = () => {
    if (!historyEvidence) return null;
    const record = historyData.find((item) => item.id === historyEvidence.recordId);
    const api = evidenceApis[historyEvidence.recordId];
    if (!record || !api) return null;
    const { target } = historyEvidence;
    const content = historyEvidenceContent(record, target);
    const key = evidenceKeyFor(target);
    let subtitle = '';
    let nav = null;
    if (target.mode === 'batch') {
      const students = record.batch_results || [];
      const order = students.map((student, index) => (student?.content ? index : null)).filter((index) => index !== null);
      const position = order.indexOf(target.studentIndex);
      const move = (step) => {
        const next = order[position + step];
        if (next === undefined) return;
        handleSelectRecordResultTab(record.id, next);
        setHistoryEvidence({ recordId: record.id, target: { ...target, studentIndex: next }, sentence: null });
      };
      subtitle = students[target.studentIndex]?.student_name || '';
      nav = order.length > 1 ? { index: position, total: order.length, onPrev: () => move(-1), onNext: () => move(1) } : null;
    } else if (target.mode === 'multi_class') {
      subtitle = `${record.class_results?.[target.classIndex]?.name || ''} 학생 ${target.versionIndex + 1}`.trim();
    } else {
      const version = (record.versions || []).find((item, i) => (item.version || i + 1) === target.versionNumber);
      subtitle = version?.custom_label || (record.versions?.length > 1 ? `버전 ${target.versionNumber}` : '');
    }
    return (
      <SaenggibuEvidenceDialog
        open
        onClose={() => setHistoryEvidence(null)}
        subtitle={subtitle}
        content={content}
        evidence={api.get(key, content)}
        status={api.statusOf(key, content)}
        initialSentence={historyEvidence.sentence}
        nav={nav}
        canRevise={!batchRefineLoading && !batchRefineApplying}
        onRequestRevision={(payload) => requestHistoryEvidenceRevision(record, target, payload)}
        onApply={(next) => applyHistoryEvidenceRevision(record, target, next)}
        surface="history"
      />
    );
  };

  return (
    <div className="sh-container">
      {historyData
        .filter((record) => expandedIds.includes(record.id) && supportsEvidence(record))
        .map((record) => (
          <SaenggibuEvidenceLoader
            key={record.id}
            recordId={record.id}
            entries={historyEvidenceEntries(record)}
            onChange={handleEvidenceApi}
          />
        ))}
      {renderHistoryEvidenceDialog()}
      {/* 헤더 */}
      <div className="sh-header">
        <div className="sh-header-row">
          <h2 className="sh-title">
            생성 기록
            {historyData.length > 0 && (
              <span className="sh-title-count">
                {selectedIds.length > 0 ? `${selectedIds.length}개 선택` : `${filteredData.length}개`}
              </span>
            )}
          </h2>
        </div>
      </div>

      {/* 검색 + 학년도/학기 필터 (최상단 - 모든 하위 필터에 영향) */}
      {!isLoading && historyData.length > 0 && (
        <div className="sh-search-row">
          <div className="sh-search-wrap">
            <svg className="sh-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="sh-search-input"
              type="text"
              placeholder="프롬프트, 학생명, 내용으로 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="sh-search-clear" onClick={() => setSearchQuery('')}>&times;</button>
            )}
          </div>
          {availableYears.length > 0 && (
            <div className="sh-semester-selects">
              <select
                className={`sh-select ${selectedAcademicYear !== '전체' ? 'sh-select--active' : ''}`}
                value={selectedAcademicYear}
                onChange={(e) => { setSelectedAcademicYear(e.target.value); setSelectedSemester('전체'); }}
              >
                <option value="전체">전체 년도</option>
                {availableYears.map(y => (
                  <option key={y} value={String(y)}>{y}년</option>
                ))}
              </select>
              {selectedAcademicYear !== '전체' && (
                <select
                  className={`sh-select ${selectedSemester !== '전체' ? 'sh-select--active' : ''}`}
                  value={selectedSemester}
                  onChange={(e) => setSelectedSemester(e.target.value)}
                >
                  <option value="전체">전체 학기</option>
                  <option value="1">1학기</option>
                  <option value="2">2학기</option>
                </select>
              )}
            </div>
          )}
        </div>
      )}

      {/* 카테고리 필터 탭 */}
      {!isLoading && historyData.length > 0 && (
        <div className="sh-filter-tabs">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              className={`sh-filter-tab ${selectedFilter === tab.key ? 'sh-filter-tab--active' : ''}`}
              onClick={() => { setSelectedFilter(tab.key); setSelectedSubFilter('전체'); setShowAllSubChips(false); setSelectedIds([]); }}
            >
              {tab.label}
              <span className="sh-filter-tab-count">{categoryCounts[tab.key]}</span>
            </button>
          ))}
        </div>
      )}

      {/* 서브카테고리 필터 칩 */}
      {!isLoading && subCategoryChips.length > 0 && (() => {
        const MAX_VISIBLE = 8;
        const visibleChips = showAllSubChips ? subCategoryChips : subCategoryChips.slice(0, MAX_VISIBLE);
        const hasMore = subCategoryChips.length > MAX_VISIBLE;
        return (
          <div className="sh-sub-filter-chips">
            <button
              className={`sh-sub-chip ${selectedSubFilter === '전체' ? 'sh-sub-chip--active' : ''}`}
              onClick={() => setSelectedSubFilter('전체')}
            >전체</button>
            {visibleChips.map(chip => (
              <button
                key={chip.label}
                className={`sh-sub-chip ${selectedSubFilter === chip.label ? 'sh-sub-chip--active' : ''}`}
                onClick={() => setSelectedSubFilter(chip.label)}
              >
                {chip.label}
                <span className="sh-sub-chip-count">{chip.count}</span>
              </button>
            ))}
            {hasMore && !showAllSubChips && (
              <button className="sh-sub-chip sh-sub-chip--more" onClick={() => setShowAllSubChips(true)}>
                +{subCategoryChips.length - MAX_VISIBLE}개 더보기
              </button>
            )}
          </div>
        );
      })()}

      {/* 검색 결과 안내 */}
      {searchQuery.trim() && (
        <div className="sh-search-result">
          &lsquo;{searchQuery.trim()}&rsquo; 검색 결과 {filteredData.length}개
        </div>
      )}

      {/* 액션 바 */}
      {!isLoading && filteredData.length > 0 && (
        <div className="sh-actions">
          <button className="sh-action-btn sh-action-btn--select" onClick={handleToggleSelectAll}>
            {filteredData.length > 0 && filteredData.every(r => selectedIds.includes(r.id)) ? '선택 해제' : '전체 선택'}
          </button>
          {selectedIds.length > 0 && (
            <button className="sh-action-btn sh-action-btn--delete" onClick={handleOpenDeleteConfirm}>
              삭제 ({selectedIds.length})
            </button>
          )}
          <button className="sh-action-btn sh-action-btn--excel" onClick={handleExcelDownload} disabled={isDownloadingExcel}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {isDownloadingExcel ? '준비 중...' : (selectedIds.length > 0 ? `다운로드 (${selectedIds.length})` : '엑셀')}
          </button>
        </div>
      )}

      {/* 스켈레톤 로딩 */}
      {isLoading && historyData.length === 0 && (
        <div className="sh-list">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="sh-skeleton-card">
              <div className="sh-skeleton-line sh-skeleton-line--medium" />
              <div className="sh-skeleton-line sh-skeleton-line--short" />
            </div>
          ))}
        </div>
      )}

      {/* 빈 상태 */}
      {!isLoading && historyData.length === 0 && (
        <div className="sh-empty">
          <div className="sh-empty-icon"><FileIcon size={28} color="#9ca3af" /></div>
          <h3 className="sh-empty-title">아직 기록이 없어요</h3>
          <p className="sh-empty-desc">생기부를 만들면 여기에 저장돼요.</p>
          <button className="sh-empty-btn" onClick={() => navigate('/create-saenggibu')}>
            만들어보기
          </button>
        </div>
      )}

      {/* 필터된 결과 없음 - Value First: 혜택 먼저 + CTA */}
      {!isLoading && historyData.length > 0 && filteredData.length === 0 && (
        <div className="sh-empty">
          <div className="sh-empty-icon"><FileIcon size={28} color="#9ca3af" /></div>
          <h3 className="sh-empty-title">{FILTER_TABS.find(t => t.key === selectedFilter)?.label || selectedFilter} 기록이 없어요</h3>
          <p className="sh-empty-desc">AI로 빠르게 만들어볼 수 있어요.</p>
          <button className="sh-empty-btn" onClick={() => navigate('/create-saenggibu')}>
            만들어보기
          </button>
        </div>
      )}

      {/* 카드 리스트 (날짜 그룹핑) */}
      {filteredData.length > 0 && (
        <div className="sh-list">
          {groupedData.map((item, idx) => {
            if (item.type === 'header') {
              return <div key={`h-${idx}`} className="sh-date-group">{item.label}</div>;
            }
            const record = item.data;
            const isExpanded = expandedIds.includes(record.id);
            const isSelected = selectedIds.includes(record.id);

            return (
              <div
                key={record.id}
                className={`sh-card ${isSelected ? 'sh-card--selected' : ''}`}
              >
                <div
                  className="sh-card-body"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => toggleExpand(record.id)}
                  onKeyDown={(e) => {
                    // 내부 버튼(저장/취소/펼치기 등)의 키 입력까지 잡으면 버튼 동작이 막히므로 카드 자신만 처리
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleExpand(record.id);
                    }
                  }}
                >
                  <div
                    className={`sh-checkbox ${isSelected ? 'sh-checkbox--checked' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleToggleSelect(record.id); }}
                  >
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                        <path d="M11.6667 3.5L5.25 9.91667L2.33333 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div className={`sh-card-initial sh-card-initial--${record.category || '행동발달'}`}>{getCategoryInitial(record.category)}</div>
                  <div className="sh-card-info">
                    <div className="sh-card-title-row">
                      {editingRecordTitleId === record.id ? (
                        <span className="sh-record-title-edit" onClick={(e) => e.stopPropagation()}>
                          <input
                            className="sh-record-title-input"
                            value={editingRecordTitleValue}
                            onChange={(e) => setEditingRecordTitleValue(e.target.value)}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === 'Enter') handleSaveRecordTitleEdit(record.id);
                              if (e.key === 'Escape') handleCancelRecordTitleEdit();
                            }}
                            placeholder="예: 2-3반 홍길동 행동발달"
                            maxLength={80}
                            autoFocus
                          />
                          <button
                            type="button"
                            className="sh-record-title-action sh-record-title-action--save"
                            disabled={savingRecordTitleId === record.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveRecordTitleEdit(record.id);
                            }}
                          >
                            저장
                          </button>
                          <button
                            type="button"
                            className="sh-record-title-action"
                            disabled={savingRecordTitleId === record.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelRecordTitleEdit();
                            }}
                          >
                            취소
                          </button>
                        </span>
                      ) : (
                        <>
                          <p className="sh-card-title">{getCardTitle(record)}</p>
                          <button
                            type="button"
                            className="sh-record-title-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartRecordTitleEdit(record);
                            }}
                            title="기록 이름 수정"
                            aria-label="기록 이름 수정"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                    <p className="sh-card-subtitle">{getCardSubtitle(record)}</p>
                  </div>
                  <div className="sh-card-right">
                    <span className="sh-card-date">{formatDate(record.created_at)}</span>
                    <span className="sh-card-count">
                      {getRecordCountLabel(record)}
                      {record.uploaded_files_count > 0 && (
                        <span className="sh-card-file-mark" aria-label={`파일 ${record.uploaded_files_count}개 참고`}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                          </svg>
                        </span>
                      )}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`sh-card-toggle ${isExpanded ? 'sh-card-toggle--open' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(record.id);
                    }}
                    aria-label={isExpanded ? '기록 접기' : '기록 펼치기'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </div>

                {/* 확장 영역 - 단일 모드 */}
                {isExpanded && record.mode !== 'batch' && record.versions?.length > 0 && (
                  <div className="sh-expanded">
                    {renderExpandedMeta(getExpandedMetaItems(record, [
                      `${record.versions.length}${record.category === '비슷한문장' || record.mode === 'similar' ? '문장' : '버전'}`,
                      `${calculateNEISBytes(record.primary_content || '')}B`,
                    ]))}

                    <div className="sh-section">
                      <p className="sh-section-label">생성 결과 ({record.versions.length}개)</p>
                      {record.versions.map((version, i) => {
                        const versionNumber = version.version || i + 1;
                        const editTarget = {
                          recordId: record.id,
                          mode: 'single',
                          versionNumber,
                        };
                        const isEditingContent = getContentEditKey(contentEditTarget) === getContentEditKey(editTarget);
                        return (
                        <div key={versionNumber} className="sh-version">
                          <div className="sh-version-header">
                            <span className="sh-version-title">
                              {editingVersionLabel?.recordId === record.id && editingVersionLabel?.versionNumber === version.version ? (
                                <span className="sh-version-label-edit" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    className="sh-version-label-input"
                                    value={editingVersionLabelValue}
                                    onChange={(e) => setEditingVersionLabelValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      e.stopPropagation();
                                      if (e.key === 'Enter') handleSaveVersionLabelEdit();
                                      if (e.key === 'Escape') handleCancelVersionLabelEdit();
                                    }}
                                    placeholder={`버전 ${version.version}`}
                                    maxLength={60}
                                    autoFocus
                                  />
                                  <button
                                    type="button"
                                    className="sh-version-label-action sh-version-label-action--save"
                                    disabled={savingVersionLabel?.recordId === record.id && savingVersionLabel?.versionNumber === version.version}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSaveVersionLabelEdit();
                                    }}
                                  >
                                    저장
                                  </button>
                                  <button
                                    type="button"
                                    className="sh-version-label-action"
                                    disabled={savingVersionLabel?.recordId === record.id && savingVersionLabel?.versionNumber === version.version}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelVersionLabelEdit();
                                    }}
                                  >
                                    취소
                                  </button>
                                </span>
                              ) : (
                                <span className="sh-version-label-display">
                                  {getVersionLabel(version)}
                                  <button
                                    type="button"
                                    className="sh-version-label-edit-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStartVersionLabelEdit(record.id, version);
                                    }}
                                    title="버전 이름 수정"
                                    aria-label={`${getVersionLabel(version)} 이름 수정`}
                                  >
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                                    </svg>
                                  </button>
                                </span>
                              )}
                              {shouldShowResultLevel(record, version.level) && (
                                <span
                                  className="sh-badge"
                                  style={{
                                    ...getLevelBadgeStyle(version.level),
                                    marginLeft: '6px'
                                  }}
                                >
                                  {version.level}
                                </span>
                              )}
                              <span className="sh-badge sh-badge--bytes">{calculateNEISBytes(version.content || '')}B</span>
                            </span>
                            <div className="sh-version-actions">
                              <button
                                type="button"
                                className="sh-version-btn sh-version-btn--copy"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenBatchRefine(record, i, {
                                    versionIndex: i,
                                    versionNumber,
                                  });
                                }}
                              >AI로 다듬기</button>
                              <button
                                className="sh-version-btn sh-version-btn--primary"
                                onClick={(e) => { e.stopPropagation(); handleCopy(version.content, `버전 ${version.version}`, { saenggibu_category: record.category, is_batch: false, version_index: i }); }}
                              >복사</button>
                              <SaenggibuResultMoreMenu
                                items={[
                                  {
                                    key: 'manual_edit',
                                    label: isEditingContent ? '수정 닫기' : '직접 수정',
                                    onSelect: () => handleStartContentEdit(editTarget, version.content || ''),
                                  },
                                  {
                                    key: 'similar',
                                    label: '비슷한 문장',
                                    onSelect: () => navigate('/create-saenggibu', {
                                      state: {
                                        prompt: version.content,
                                        category: '비슷한문장',
                                        sourceRecordId: record.id,
                                        categoryHint: record.category === '비슷한문장'
                                          ? (record.sub_category || '')
                                          : (record.category || ''),
                                        targetBytes: calculateNEISBytes(version.content || ''),
                                      }
                                    }),
                                  },
                                  {
                                    key: 'delete',
                                    label: '삭제',
                                    tone: 'danger',
                                    onSelect: () => setShowVersionDeleteConfirm({ recordId: record.id, versionNumber: version.version }),
                                  },
                                ]}
                                saveExample={{
                                  text: version.content,
                                  category: record.category,
                                  categoryHint: record.sub_category,
                                  wasEdited: manuallyEditedKeys.has(getContentEditKey(editTarget)),
                                  isPro: isProUser,
                                  versionIndex: i,
                                }}
                              />
                            </div>
                          </div>
                          {renderEditableContent(record, editTarget, version.content || '')}
                          {renderHistoryRefinePanel(record, i, version, {
                            versionIndex: i,
                            versionNumber,
                          })}
                        </div>
                        );
                      })}
                    </div>

                    <div className="sh-section">
                      <p className="sh-section-label">생성 맥락</p>
                      <div className="sh-context-summary">
                        {record.emphasize_points?.length > 0 && (
                          <div className="sh-emphasis-tags">
                            {record.emphasize_points.map((p, i) => (
                              <span key={i} className="sh-emphasis-tag">{p}</span>
                            ))}
                          </div>
                        )}
                        {record.prompt && (
                          <details className="sh-batch-context-detail">
                            <summary>입력한 내용 보기</summary>
                            <div className="sh-prompt-full">{record.prompt}</div>
                          </details>
                        )}
                      </div>
                    </div>

                    <button
                      className="sh-reuse-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/create-saenggibu', {
                          state: {
                            prompt: record.prompt,
                            category: record.category || '행동발달',
                            emphasizePoints: record.emphasize_points || [],
                            schoolLevel: record.school_level || '중등',
                            levelCounts: record.level_counts || null,
                            sourceRecordId: record.id,
                            recordId: record.id,
                            categoryHint: record.category === '비슷한문장'
                              ? (record.sub_category || '')
                              : (record.category || ''),
                            targetBytes: record.target_word_count || calculateNEISBytes(record.prompt || ''),
                            generatedVersions: (
                              record.category === '비슷한문장' || record.mode === 'similar'
                            ) ? (record.versions || []) : undefined,
                            similarCount: record.generation_count || record.total_versions || 10,
                            similarVariationMode: record.variation_mode || 'structure_keep',
                            similarInstruction: record.similar_instruction || '',
                            similarLevelDistribution: record.level_distribution || null,
                            uploadedFilesMeta: Array.isArray(record.uploaded_files_meta)
                              ? record.uploaded_files_meta
                              : [],
                            achievementStandardRefs: record.achievement_standard_refs || [],
                            achievementStandardKeys: record.achievement_standard_keys || [],
                          }
                        });
                      }}
                    >
                      {record.category === '비슷한문장' || record.mode === 'similar'
                        ? '이 문장으로 다시 만들기'
                        : '이 프롬프트로 다시 만들기'}
                    </button>
                  </div>
                )}

                {/* 확장 영역 - 다반(multi_class) 모드 */}
                {isExpanded && record.mode === 'multi_class' && record.class_results?.length > 0 && (() => {
                  const classIdx = activeBatchStudentIdx[record.id] || 0;
                  const activeClass = record.class_results[classIdx] || record.class_results[0];

                  return (
                    <div className="sh-expanded">
                      {renderExpandedMeta(getExpandedMetaItems(record, [
                        `${record.class_results.length}개 반`,
                        `총 ${record.total_students || record.class_results.reduce((sum, c) => sum + (c.versions?.length || c.total_count || 0), 0)}명`,
                      ]))}
                      <div className="sh-section">
                        <p className="sh-section-label">반 선택</p>
                        <div className="ih-student-tabs">
                          {record.class_results.map((classItem, cIdx) => (
                            <button
                              key={classItem.id || cIdx}
                              className={`ih-student-tab ${classIdx === cIdx ? 'ih-student-tab--active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectRecordResultTab(record.id, cIdx);
                              }}
                            >
                              {classItem.name || `${cIdx + 1}반`}
                            </button>
                          ))}
                        </div>
                      </div>
                      {activeClass && (
                        <div className="sh-section">
                          <p className="sh-section-label">{activeClass.name || '반'} 생성 결과 ({activeClass.versions?.length || 0}명)</p>
                          {(activeClass.versions || []).map((version, vIdx) => {
                            const editTarget = {
                              recordId: record.id,
                              mode: 'multi_class',
                              classIndex: classIdx,
                              versionIndex: vIdx,
                            };
                            const isEditingContent = getContentEditKey(contentEditTarget) === getContentEditKey(editTarget);
                            return (
                            <div key={version.version || vIdx} className="sh-version">
                              <div className="sh-version-header">
                                <span className="sh-version-title">
                                  학생 {vIdx + 1}
                                  {version.level && (
                                    <span className="sh-badge" style={{ ...getLevelBadgeStyle(version.level), marginLeft: '6px' }}>
                                      {version.level}
                                    </span>
                                  )}
                                  <span className="sh-badge sh-badge--bytes">{calculateNEISBytes(version.content || '')}B</span>
                                </span>
                                <div className="sh-version-actions">
                                  <button
                                    type="button"
                                    className="sh-version-btn sh-version-btn--copy"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenBatchRefine(record, vIdx, {
                                        classIndex: classIdx,
                                        versionIndex: vIdx,
                                      });
                                    }}
                                  >AI로 다듬기</button>
                                  <button
                                    className="sh-version-btn sh-version-btn--primary"
                                    onClick={(e) => { e.stopPropagation(); handleCopy(version.content, `${activeClass.name} 학생 ${vIdx + 1}`, { saenggibu_category: record.category, is_batch: false, version_index: vIdx }); }}
                                  >복사</button>
                                  <SaenggibuResultMoreMenu
                                    items={[
                                      {
                                        key: 'manual_edit',
                                        label: isEditingContent ? '수정 닫기' : '직접 수정',
                                        onSelect: () => handleStartContentEdit(editTarget, version.content || ''),
                                      },
                                    ]}
                                    saveExample={{
                                      text: version.content,
                                      category: record.category,
                                      categoryHint: record.sub_category,
                                      wasEdited: manuallyEditedKeys.has(getContentEditKey(editTarget)),
                                      isPro: isProUser,
                                      versionIndex: vIdx,
                                    }}
                                  />
                                </div>
                              </div>
                              {renderEditableContent(record, editTarget, version.content || '')}
                              {renderHistoryRefinePanel(record, vIdx, version, {
                                classIndex: classIdx,
                                versionIndex: vIdx,
                              })}
                            </div>
                            );
                          })}
                        </div>
                      )}
                      {record.prompt && (
                        <div className="sh-section">
                          <p className="sh-section-label">생성 맥락</p>
                          <details className="sh-batch-context-detail">
                            <summary>입력한 내용 보기</summary>
                            <div className="sh-prompt-full">{record.prompt}</div>
                          </details>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* 확장 영역 - 일괄(batch) 모드 */}
                {isExpanded && record.mode === 'batch' && record.batch_results?.length > 0 && (() => {
                  const studentIdx = activeBatchStudentIdx[record.id] || 0;
                  const activeStudent = record.batch_results[studentIdx];
                  const editTarget = {
                    recordId: record.id,
                    mode: 'batch',
                    studentIndex: studentIdx,
                  };
                  const isEditingContent = getContentEditKey(contentEditTarget) === getContentEditKey(editTarget);

                  return (
                    <div className="sh-expanded">
                      {renderExpandedMeta(getExpandedMetaItems(record, [
                        `${record.batch_results.length}명`,
                        record.successful_count !== record.batch_results.length ? `성공 ${record.successful_count || record.batch_results.length}명` : '',
                      ]))}
                      <div className="sh-section">
                        <p className="sh-section-label">학생 선택 ({record.batch_results.length}명)</p>
                        <div className="ih-student-tabs">
                          {record.batch_results.map((student, sIdx) => (
                            <button
                              key={sIdx}
                              className={`ih-student-tab ${studentIdx === sIdx ? 'ih-student-tab--active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectRecordResultTab(record.id, sIdx);
                              }}
                            >
                              {student.student_name}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* 선택된 학생 결과 */}
                      {activeStudent && (
                        <div className="sh-section">
                          <div className="sh-version">
                            <div className="sh-version-header">
                              <span className="sh-version-title">
                                {editingBatchStudentName?.recordId === record.id && editingBatchStudentName?.studentIndex === studentIdx ? (
                                  <span className="sh-student-name-edit" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      className="sh-student-name-input"
                                      value={editingBatchStudentNameValue}
                                      onChange={(e) => setEditingBatchStudentNameValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter') handleSaveBatchStudentNameEdit();
                                        if (e.key === 'Escape') handleCancelBatchStudentNameEdit();
                                      }}
                                      placeholder="학생 이름"
                                      maxLength={40}
                                      autoFocus
                                    />
                                    <button
                                      type="button"
                                      className="sh-student-name-action sh-student-name-action--save"
                                      disabled={savingBatchStudentName?.recordId === record.id && savingBatchStudentName?.studentIndex === studentIdx}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveBatchStudentNameEdit();
                                      }}
                                    >
                                      저장
                                    </button>
                                    <button
                                      type="button"
                                      className="sh-student-name-action"
                                      disabled={savingBatchStudentName?.recordId === record.id && savingBatchStudentName?.studentIndex === studentIdx}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelBatchStudentNameEdit();
                                      }}
                                    >
                                      취소
                                    </button>
                                  </span>
                                ) : (
                                  <span className="sh-student-name-display">
                                    {activeStudent.student_name}
                                    <button
                                      type="button"
                                      className="sh-student-name-edit-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStartBatchStudentNameEdit(record.id, studentIdx, activeStudent.student_name);
                                      }}
                                      title="학생 이름 수정"
                                      aria-label={`${activeStudent.student_name} 이름 수정`}
                                    >
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                                      </svg>
                                    </button>
                                  </span>
                                )}
                                <span className="sh-badge sh-badge--bytes">{activeStudent.byte_count || calculateNEISBytes(activeStudent.content || '')}B</span>
                                {activeStudent.target_bytes > 0 && (
                                  <span className="sh-badge sh-badge--version">목표 {activeStudent.target_bytes}B</span>
                                )}
                              </span>
                              <div className="sh-version-actions">
                                <button
                                  className="sh-version-btn sh-version-btn--copy"
                                  onClick={(e) => { e.stopPropagation(); handleOpenBatchRefine(record, studentIdx); }}
                                >AI로 다듬기</button>
                                <button
                                  className="sh-version-btn sh-version-btn--primary"
                                  onClick={(e) => { e.stopPropagation(); handleCopy(activeStudent.content, activeStudent.student_name, { saenggibu_category: record.category, is_batch: true, version_index: studentIdx }); }}
                                >복사</button>
                                <SaenggibuResultMoreMenu
                                  items={[
                                    {
                                      key: 'manual_edit',
                                      label: isEditingContent ? '수정 닫기' : '직접 수정',
                                      onSelect: () => handleStartContentEdit(editTarget, activeStudent.content || ''),
                                    },
                                  ]}
                                  saveExample={{
                                    text: activeStudent.content,
                                    category: record.category,
                                    categoryHint: record.sub_category,
                                    wasEdited: manuallyEditedKeys.has(getContentEditKey(editTarget)),
                                    isPro: isProUser,
                                    isBatch: true,
                                    versionIndex: studentIdx,
                                  }}
                                />
                              </div>
                            </div>
                            {renderEditableContent(
                              record,
                              editTarget,
                              activeStudent.content || '',
                              activeStudent.target_bytes
                            )}
                            {renderHistoryRefinePanel(record, studentIdx, activeStudent)}
                          </div>
                        </div>
                      )}
                      {(record.batch_input_context || record.teacher_instruction_present || record.reference_text_present || record.reference_files_count > 0) && (
                        <div className="sh-section">
                          <p className="sh-section-label">생성 맥락</p>
                          <div className="sh-batch-context">
                            {record.batch_input_context?.students?.length > 0 && (
                              <div className="sh-batch-context-row">
                                <span>학생 기록</span>
                                <strong>{record.batch_input_context.students.length}명 입력 내용 저장됨</strong>
                              </div>
                            )}
                            {(record.reference_text_present || record.reference_files_count > 0 || record.batch_input_context?.reference_text) && (
                              <div className="sh-batch-context-row">
                                <span>평가계획서</span>
                                <strong>{record.reference_files_count > 0 ? `${record.reference_files_count}개 파일 참고` : '텍스트 참고'}</strong>
                              </div>
                            )}
                            {record.batch_input_context?.reference_text && (
                              <details className="sh-batch-context-detail">
                                <summary>평가계획서 내용 보기</summary>
                                <pre>{record.batch_input_context.reference_text}</pre>
                              </details>
                            )}
                            {(record.teacher_instruction_present || record.batch_input_context?.teacher_instruction) && (
                              <div className="sh-batch-context-row">
                                <span>요청사항</span>
                                <strong>{record.batch_input_context?.teacher_instruction || '요청사항 있음'}</strong>
                              </div>
                            )}
                            <button
                              type="button"
                              className="sh-reuse-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReuseBatchRecord(record);
                              }}
                            >
                              이 입력으로 다시 만들기
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

              </div>
            );
          })}

          {/* 더보기 */}
          {hasMore && historyData.length >= HISTORY_PAGE_SIZE && historyData.length < MAX_LOADED_HISTORY_RECORDS && (
            <button className="sh-load-more" onClick={handleLoadMore} disabled={isLoading}>
              {isLoading ? '불러오는 중...' : '더보기'}
            </button>
          )}
        </div>
      )}

      {/* 삭제 확인 모달 - UX Writing: 쉬운 말, 해요체 */}
      {showDeleteConfirm && (
        <div className="sh-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="sh-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="sh-modal-title">
              {selectedIds.length === 1 ? '이 기록을 삭제할까요?' : `${selectedIds.length}개 기록을 삭제할까요?`}
            </h3>
            <p className="sh-modal-desc">삭제하면 다시 복구할 수 없어요.</p>
            <div className="sh-modal-actions">
              <button className="sh-modal-btn sh-modal-btn--cancel" onClick={() => setShowDeleteConfirm(false)}>
                취소
              </button>
              <button className="sh-modal-btn sh-modal-btn--delete" onClick={handleDeleteSelected}>
                삭제하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 버전 삭제 확인 모달 */}
      {showVersionDeleteConfirm && (
        <div className="sh-modal-overlay" onClick={() => setShowVersionDeleteConfirm(null)}>
          <div className="sh-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="sh-modal-title">버전 {showVersionDeleteConfirm.versionNumber}을 삭제할까요?</h3>
            <p className="sh-modal-desc">삭제하면 다시 복구할 수 없어요.</p>
            <div className="sh-modal-actions">
              <button className="sh-modal-btn sh-modal-btn--cancel" onClick={() => setShowVersionDeleteConfirm(null)}>
                취소
              </button>
              <button
                className="sh-modal-btn sh-modal-btn--delete"
                onClick={() => handleDeleteVersion(showVersionDeleteConfirm.recordId, showVersionDeleteConfirm.versionNumber)}
              >
                삭제하기
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SaenggibuHistory;
